import { buildWAUrl } from '../lib/whatsapp';

const q = buildWAUrl;

// -- Base types ----------------------------------------------------------------

export type PackageCategory = 'honeymoon' | 'family' | 'beach' | 'short' | 'spiritual';

export interface Package {
  id: string;
  title: string;
  badge: string;
  badgeColor: 'coral' | 'gold' | 'teal' | 'navy' | 'green';
  location: string;
  nights: number;
  days: number;
  description: string;
  highlights: string[];
  priceFromAED: number;
  image: string;
  imageAlt: string;
  categories: PackageCategory[];
  waText: string;
}

// -- Detail-page extended types ------------------------------------------------

export type ItineraryDay = {
  day: number;
  title: string;
  description: string;
  meals: ('Breakfast' | 'Lunch' | 'Dinner')[];
  accommodation?: string;
};

export type PackageDetail = Package & {
  heroGallery: { url: string; alt: string }[];
  longDescription: string;
  highlights: string[];         // overrides base (5-8 items on detail page)
  itinerary: ItineraryDay[];
  inclusions: string[];
  exclusions: string[];
  hotelExamples: { name: string; city: string; rating: number }[];
  bestTimeToVisit: string;
  visaInfo: string;
  flightDuration: string;
  departureCities: string[];
  groupSize: string;
  whatToBring: string[];
  faqs: { q: string; a: string }[];
  cancellationPolicy: string;
  depositAED: number;
};

// -- Supporting interfaces -----------------------------------------------------

export interface Destination {
  name: string;
  tagline: string;
  image: string;
  imageAlt: string;
  href: string;
  waText: string;
}

export interface PriceRow {
  destination: string;
  duration: string;
  priceAED: number;
  type: string;
  popular?: boolean;
}

export interface Testimonial {
  name: string;
  origin: string;
  text: string;
  rating: number;
  packageName: string;
}

// -- Full package detail data --------------------------------------------------

export const PACKAGE_DETAILS: PackageDetail[] = [
  // -- MALDIVES ---------------------------------------------------------------
  {
    id: 'maldives',
    title: 'Maldives Honeymoon Escape',
    badge: 'Best Seller',
    badgeColor: 'coral',
    location: 'Maldives',
    nights: 5,
    days: 6,
    description: 'Overwater villas, crystal lagoons, and private beach dinners - the ultimate honeymoon experience from UAE.',
    priceFromAED: 3499,
    image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=800&q=80&fm=webp',
    imageAlt: 'Overwater villas at sunset in the Maldives lagoon',
    categories: ['honeymoon', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Maldives Honeymoon Escape (5N/6D). Please share details and current pricing.',
    depositAED: 1050,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Aerial view of Maldives atoll with overwater villas',
      },
      {
        url: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'White sand beach and turquoise water in the Maldives',
      },
      {
        url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Snorkelling over a coral reef in the Maldives',
      },
      {
        url: 'https://images.unsplash.com/photo-1540202404-a2f29016b523?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Palm tree on a tropical sandbar in the Maldives',
      },
    ],
    longDescription: `The Maldives archipelago, scattered across the Indian Ocean southwest of Sri Lanka, is the world's most sought-after honeymoon destination - and from the UAE, it's only four hours away. This five-night package places you in a carefully selected resort island where overwater villas extend directly above a glass-flat lagoon, and the only sounds are breaking waves and the occasional passing sea turtle.

Your stay is built around the pace of island life: mornings spent snorkelling the house reef, afternoons on the villa sundeck watching boats drift past, evenings at long tables with the ocean below. The package includes a seaplane transfer from Male airport - the 30-minute flight over the atoll, with its patchwork of turquoise and deep blue, is arguably the most memorable part of the entire journey.

We handle every detail from the moment you land in Male: immigration assistance, airport-to-resort coordination, and special arrangements for honeymooners including flower decoration, couples' dinner on the beach, and a fruit basket welcome. This package is designed for couples but can be adapted for families staying in garden villas or beach bungalows.`,
    highlights: [
      'Overwater villa with direct ocean access and glass floor panel',
      'Seaplane round-trip Male <-> resort included (most scenic 30 minutes of the trip)',
      'Sunset dhow cruise with live music and dinner',
      'Guided snorkelling on the house reef with resident marine biologist',
      'Daily breakfast at the resort restaurant with ocean views',
      'Honeymooner setup: flower decoration, champagne, and beach dinner',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival & Seaplane Transfer',
        description: 'Arrive at Male International Airport (MLE). Our representative meets you at arrivals, assists with immigration, and coordinates your seaplane departure. Fly over the atoll - a 30-minute flight that sets the tone for everything ahead. Check into your overwater villa, freshen up, and head to the sunset dinner.',
        meals: ['Dinner'],
        accommodation: 'Overwater Villa, Resort Island',
      },
      {
        day: 2,
        title: 'House Reef Snorkelling',
        description: 'Morning guided snorkel on the resort\'s house reef with the resident marine biologist - turtles, reef sharks, and parrotfish are common sightings. Afternoon entirely free: lounge on the villa deck, order from the in-villa menu, or book a massage at the overwater spa. Evening: dinner at the main restaurant.',
        meals: ['Breakfast'],
        accommodation: 'Overwater Villa, Resort Island',
      },
      {
        day: 3,
        title: 'Dolphin Cruise & Optional Diving',
        description: 'Optional scuba introduction at the dive centre for non-certified guests (equipment included). Afternoon free. At sunset, board a traditional dhow for the included sunset cruise - an hour on the open water with canapes and the sky turning pink above the atoll. Dinner on the dhow under the stars.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Overwater Villa, Resort Island',
      },
      {
        day: 4,
        title: 'Local Island Excursion',
        description: 'Half-day speedboat excursion to a nearby inhabited island - see how Maldivian families live, visit the local school, and browse the small market. Sandbar picnic lunch included. Back at the resort by 3pm for an afternoon of watersports: kayak, paddleboard, or glass-bottom boat rental.',
        meals: ['Breakfast', 'Lunch'],
        accommodation: 'Overwater Villa, Resort Island',
      },
      {
        day: 5,
        title: 'Spa & Beach Day',
        description: 'Your last full day - make it count. Morning: overwater spa couples\' treatment (at additional cost, bookable on arrival). Afternoon: beach day with your favourite book. Evening: the included private beach dinner - table set on the sand, torches, four-course menu, your own slice of the Indian Ocean.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Overwater Villa, Resort Island',
      },
      {
        day: 6,
        title: 'Check-out & Departure',
        description: 'Final morning on the villa deck. Check-out at 12 noon. Seaplane back to Male in the early afternoon - timing coordinated with your outbound flight. Our team confirms ground transport in Dubai.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Return economy air tickets from Dubai (DXB) or Abu Dhabi (AUH)',
      '5-night overwater villa accommodation',
      'Seaplane round-trip: Male airport <-> resort',
      'Daily breakfast at the resort restaurant',
      'Sunset dhow dinner cruise (Day 3)',
      'Guided house reef snorkelling session (Day 2)',
      'Local island excursion with sandbar picnic lunch (Day 4)',
      'Honeymooner setup: room flowers, fruit basket, welcome amenity',
      'Airport and resort meet-and-greet assistance',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'Lunches and dinners outside the package (resort dining available)',
      'Scuba diving certification courses and premium dive packages',
      'Spa treatments (bookable on-site)',
      'Travel insurance (guidance provided on request)',
      'Personal shopping and minibar charges',
    ],
    hotelExamples: [
      { name: 'Kandima Maldives', city: 'Dhaalu Atoll', rating: 4 },
      { name: 'Sun Siyam Iru Veli', city: 'Meemu Atoll', rating: 5 },
      { name: 'Meeru Island Resort', city: 'North Male Atoll', rating: 4 },
    ],
    bestTimeToVisit: 'November through April (dry season) offers calm seas, excellent snorkelling visibility, and minimal rainfall.',
    visaInfo: 'The Maldives grants a free 30-day visa on arrival to all nationalities - no advance application required.',
    flightDuration: 'approx 4hr direct from Dubai (DXB)',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)'],
    groupSize: 'Designed for couples; adaptable for families up to 6',
    whatToBring: [
      'Reef-safe sunscreen (chemical sunscreens are banned at many resorts)',
      'Snorkel mask (or rent at resort)',
      'Light cotton clothes and a kaftan',
      'Universal power adapter',
    ],
    faqs: [
      { q: 'Is the seaplane transfer included?', a: 'Yes. The round-trip seaplane transfer between Male International Airport and the resort island is included in the package price. Flight time is approximately 25-35 minutes depending on the resort.' },
      { q: 'Can we upgrade to a pool water villa?', a: 'Pool water villas are available at a supplement. Share your preference when requesting a quote and we will confirm availability and the additional cost.' },
      { q: 'Is this suitable for families with young children?', a: 'Yes. We can switch you to a family beach villa instead of an overwater villa, and select resorts with kids\' clubs and shallow pools. The itinerary adjusts accordingly.' },
      { q: 'Are meals included beyond breakfast?', a: 'Daily breakfast is included, as is the sunset dinner cruise on Day 3 and the sandbar picnic lunch on Day 4. All other lunches and dinners are at the resort\'s restaurants at your cost.' },
      { q: 'What if the weather is bad?', a: 'Maldives weather is generally excellent from November to April. Seaplanes may be delayed in very rough conditions; speedboat transfers are the fallback. We monitor conditions and keep you informed.' },
    ],
    cancellationPolicy: '50% refund for cancellations more than 30 days before departure. No refund within 30 days. Travel date changes subject to airline availability and applicable change fees.',
  },

  // -- BALI ------------------------------------------------------------------
  {
    id: 'bali',
    title: 'Bali Tropical Retreat',
    badge: 'Family & Couples',
    badgeColor: 'teal',
    location: 'Bali, Indonesia',
    nights: 6,
    days: 7,
    description: 'Temple trails, rice terraces, private pool villas, and Seminyak sunsets - Bali for every kind of traveler.',
    priceFromAED: 2899,
    image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=800&q=80&fm=webp',
    imageAlt: 'Tegallalang rice terraces in Bali with morning mist',
    categories: ['honeymoon', 'family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Bali Tropical Retreat (6N/7D). Please share details and current pricing.',
    depositAED: 870,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tanah Lot temple on a rock at sunset in Bali',
      },
      {
        url: 'https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Terraced rice paddies in Ubud Bali',
      },
      {
        url: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Private pool villa surrounded by tropical garden in Bali',
      },
      {
        url: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Uluwatu sea temple perched on a cliff in Bali',
      },
    ],
    longDescription: `Bali is the rare destination that rewards every traveler equally - couples find romance, families find adventure, and first-timers find the trip they will measure all future holidays against. From the UAE it's a seven-hour flight that feels worth every minute from the moment the warm, frangipani-scented air greets you at Ngurah Rai airport.

This six-night package moves through Bali's three distinct zones: the cultural heartland of Ubud with its rice terraces, dance performances, and artisan workshops; the spiritual south with its dramatic clifftop temples and Indian Ocean views; and the resort belt of Seminyak and Nusa Dua where beach clubs, surf schools, and sunset restaurants define the afternoon rhythm.

You'll stay in a private pool villa throughout - Bali's signature accommodation style, where a walled tropical garden creates complete privacy around a plunge pool. The villa experience is as much a part of the destination as the temples. Our local ground handler manages all inter-villa transfers, which in Bali's traffic can be the difference between seeing two temples or four.`,
    highlights: [
      'Private pool villa throughout the stay (Bali\'s signature accommodation)',
      'Tegallalang rice terrace guided walk at golden hour',
      'Tanah Lot and Uluwatu temples at sunset',
      'Kecak Fire Dance performance at Uluwatu amphitheatre',
      'Traditional Balinese cooking class in Ubud',
      'Daily breakfast served by the pool',
      'Optional Nusa Penida day trip with snorkelling at Manta Point',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival & Ubud Check-in',
        description: 'Arrive at Ngurah Rai Airport (DPS). Private transfer to your Ubud pool villa - about 1.5 hours through rice field roads and market towns. Check in, freshen up, and head to a riverside warung for your first nasi goreng of the trip. Early night ahead of a full day tomorrow.',
        meals: ['Dinner'],
        accommodation: 'Private Pool Villa, Ubud',
      },
      {
        day: 2,
        title: 'Ubud Cultural Day',
        description: 'Morning guided walk through Tegallalang rice terraces with a local farmer - learn the traditional subak irrigation system (UNESCO listed). Afternoon: Sacred Monkey Forest for the brave, then Ubud Art Market for hand-carved souvenirs. Evening: Ubud Palace traditional dance performance.',
        meals: ['Breakfast'],
        accommodation: 'Private Pool Villa, Ubud',
      },
      {
        day: 3,
        title: 'Cooking Class & Uluwatu',
        description: 'Morning: half-day Balinese cooking class - market visit, then you cook four dishes including tempeh, sate, and klepon rice balls. Afternoon: transfer south to Uluwatu cliff temple. Sunset walk along the clifftop, then the unmissable Kecak Fire Dance as the sun drops into the ocean.',
        meals: ['Breakfast', 'Lunch'],
        accommodation: 'Private Pool Villa, Ubud or Seminyak',
      },
      {
        day: 4,
        title: 'Tanah Lot Sunrise & Beach',
        description: 'Very early start for Tanah Lot temple at sunrise - misty, uncrowded, and dramatically beautiful. Transfer to Seminyak beach by 10am. Afternoon: beach clubs, surf lessons for beginners, or simply collapse into a sun lounger. Sunset at Ku De Ta or Potato Head beach bar.',
        meals: ['Breakfast'],
        accommodation: 'Private Pool Villa, Seminyak',
      },
      {
        day: 5,
        title: 'Free Day - Nusa Penida Option',
        description: 'Free day. Option 1 (recommended): Day trip to Nusa Penida island - speedboat, snorkelling at Manta Point and Crystal Bay, cliff viewpoints at Kelingking Beach. Option 2: Spa day, surfing lesson, or beach club day in Canggu. Option 3: Nusa Dua\'s calm beach if traveling with young children.',
        meals: ['Breakfast'],
        accommodation: 'Private Pool Villa, Seminyak',
      },
      {
        day: 6,
        title: 'Jimbaran Seafood Farewell',
        description: 'Relaxed morning. Optional visit to Garuda Wisnu Kencana statue for panoramic views. Afternoon: last round of souvenir shopping at Seminyak market. Evening: dinner on the sand at Jimbaran Bay - fresh grilled seafood by torchlight with the waves a few metres away. Bali\'s perfect goodbye.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Private Pool Villa, Seminyak',
      },
      {
        day: 7,
        title: 'Check-out & Departure',
        description: 'Final breakfast by the pool. Check-out at 12 noon. Private transfer to Ngurah Rai Airport (DPS). Our team confirms ground transport arrangements on the Dubai side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Return economy air tickets from Dubai (DXB)',
      '6-night private pool villa (Ubud and Seminyak areas)',
      'Airport arrival and departure transfers',
      'Daily breakfast',
      'Tegallalang rice terrace guided walk',
      'Half-day Balinese cooking class with market visit',
      'Uluwatu Kecak Fire Dance entry',
      'Tanah Lot sunrise visit',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'Bali visa on arrival fee (approx USD 35, payable at airport)',
      'Lunches and dinners outside the package',
      'Nusa Penida day trip (optional upgrade: AED 350/person)',
      'Entry fees to Monkey Forest and unscheduled attractions',
      'Travel insurance',
    ],
    hotelExamples: [
      { name: 'Komaneka at Bisma', city: 'Ubud', rating: 5 },
      { name: 'Alaya Resort Ubud', city: 'Ubud', rating: 4 },
      { name: 'The Layar Private Villas', city: 'Seminyak', rating: 5 },
    ],
    bestTimeToVisit: 'April through October (dry season) offers clear skies and calm surf; shoulder months May and September are ideal for fewer crowds.',
    visaInfo: 'Indonesian visa on arrival is available for UAE residents for stays up to 30 days. The fee is approximately USD 35, payable in cash at the airport on arrival.',
    flightDuration: 'approx 7hr from Dubai (DXB), typically one stopover via Singapore or Kuala Lumpur',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 12 travelers; larger groups on request',
    whatToBring: [
      'Modest clothing for temple visits (sarongs are provided at most temples)',
      'Mosquito repellent (DEET-based recommended for rice terrace areas)',
      'Waterproof sandals or shoes for rice terrace walks',
      'Reef-safe sunscreen',
    ],
    faqs: [
      { q: 'Do you arrange the Bali visa?', a: 'The Bali visa on arrival is paid directly by you at the airport on arrival. It costs approximately USD 35 and the queue typically takes 10-15 minutes. We brief you fully before travel.' },
      { q: 'Can we stay in Seminyak only without going to Ubud?', a: 'Yes. The itinerary is fully flexible - we can base you in Seminyak for a beach-focused stay or split between Ubud and Canggu. Let us know your preference.' },
      { q: 'Is Bali suitable for young children?', a: 'Very much so. We select family-friendly pool villas with shallow areas and child menus, and swap the Kecak Dance for more child-friendly activities if preferred.' },
      { q: 'Can I add the Nusa Penida trip?', a: 'Yes. A full-day Nusa Penida boat trip with snorkelling at Manta Point and Kelingking Beach viewpoint can be added for AED 350 per person.' },
      { q: 'Which airline do you book?', a: 'We compare Emirates, Air Arabia, FlyDubai, Batik Air, and other carriers to find the best value for your travel dates. We always brief you on the flight details before confirming.' },
    ],
    cancellationPolicy: '50% refund for cancellations more than 30 days before departure. No refund within 30 days. Visa on arrival fees are non-refundable.',
  },

  // -- TURKEY ----------------------------------------------------------------
  {
    id: 'turkey',
    title: 'Turkey Cultural Journey',
    badge: 'Top Rated',
    badgeColor: 'gold',
    location: 'Istanbul & Cappadocia',
    nights: 7,
    days: 8,
    description: 'Hot-air balloons over Cappadocia, a Bosphorus dinner cruise, and the grand bazaars of Istanbul.',
    priceFromAED: 2499,
    image: 'https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?auto=format&fit=crop&w=800&q=80&fm=webp',
    imageAlt: 'Hot air balloons rising over Cappadocia fairy chimneys at sunrise',
    categories: ['family', 'honeymoon'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Turkey Cultural Journey (7N/8D). Please share details and current pricing.',
    depositAED: 750,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Istanbul skyline with the Blue Mosque and Bosphorus at sunset',
      },
      {
        url: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Interior of the Blue Mosque in Istanbul',
      },
      {
        url: 'https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Dozens of hot air balloons over Cappadocia at sunrise',
      },
      {
        url: 'https://images.unsplash.com/photo-1569949381669-ecf31ae8e613?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Fairy chimneys and cave houses in Cappadocia valley',
      },
    ],
    longDescription: `Turkey is the rare country that delivers two completely different travel experiences within a single holiday. Istanbul - one of the world's great cities, straddling Europe and Asia across the Bosphorus - offers Hagia Sophia, the Grand Bazaar, rooftop kebab restaurants, and one of the world's great urban skylines. Cappadocia, one hour away by domestic flight, is something else entirely: a surreal landscape of eroded fairy chimneys and underground cities, best experienced from inside a hot-air balloon at sunrise.

This seven-night itinerary gives you four nights in Istanbul to cover the Ottoman imperial sights, the covered Spice Market, Galata Tower, and a private Bosphorus dinner cruise. Then three nights in Cappadocia for the morning balloon flight, the Goreme Open Air Museum with its thousand-year-old frescoes, and a guided underground city tour.

Turkey requires no visa for UAE residents - entry on Emirates ID for up to 90 days. Istanbul's food alone justifies the trip: from simit vendors at the waterfront to the best baklava in the world sold by weight in Karakoy. This package includes daily breakfast and one special dinner - the Bosphorus cruise under the bridge with live music.`,
    highlights: [
      'Sunrise hot-air balloon flight over Cappadocia fairy chimneys (1 hour)',
      'Private Bosphorus dinner cruise with live Turkish music',
      'Guided tour: Hagia Sophia, Blue Mosque, and Topkapi Palace',
      'Grand Bazaar and Spice Market guided walking tour',
      'Goreme Open Air Museum and underground city exploration',
      '4-star hotels in both Istanbul (Sultanahmet) and Cappadocia (cave hotel)',
      'No visa required for UAE residents',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrive Istanbul',
        description: 'Arrive at Istanbul Airport (IST). Transfer to your hotel in the Sultanahmet district - a 40-minute drive. Check in and take an evening stroll along the Bosphorus waterfront. Dinner at a rooftop restaurant with views of the illuminated mosques.',
        meals: ['Dinner'],
        accommodation: '4-Star Hotel, Sultanahmet, Istanbul',
      },
      {
        day: 2,
        title: 'Old Istanbul - The Imperial Core',
        description: 'Full-day guided tour with a licensed local guide: Hagia Sophia (UNESCO) in the morning before the crowds, the Blue Mosque (Sultan Ahmed Mosque) across the plaza, underground Basilica Cistern, and Topkapi Palace with views across the Bosphorus to Asia. Evening free in Sultanahmet.',
        meals: ['Breakfast'],
        accommodation: '4-Star Hotel, Sultanahmet, Istanbul',
      },
      {
        day: 3,
        title: 'Grand Bazaar & Bosphorus Cruise',
        description: 'Morning at the Grand Bazaar - the world\'s oldest covered market with 4,000 shops across 60 streets. Spice Market (Misir Carsisi) nearby for Turkish teas, lokum, and spices. Afternoon free for Galata Tower or the modern Beyoglu district. Evening: private Bosphorus dinner cruise - 2.5 hours under the bridges with live music.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: '4-Star Hotel, Sultanahmet, Istanbul',
      },
      {
        day: 4,
        title: 'Istanbul -> Cappadocia',
        description: 'Free morning in Istanbul - Karakoy for the world\'s best baklava, or a ferry ride to the Asian side. Afternoon domestic flight to Nevsehir or Kayseri (Cappadocia). Transfer to your cave hotel in Goreme. Sunset walk through the valley.',
        meals: ['Breakfast'],
        accommodation: '4-Star Cave Hotel, Goreme, Cappadocia',
      },
      {
        day: 5,
        title: 'Balloon at Dawn & Valley Exploration',
        description: 'Wake-up call at 4:30am. Transfer to launch site. One-hour hot-air balloon flight over the fairy chimneys as the sun rises - the defining image of Turkey. Champagne toast on landing. Back to hotel for breakfast. Afternoon: Goreme Open Air Museum (10th-century rock churches), Devrent Valley, and Pasabag Monks Valley.',
        meals: ['Breakfast'],
        accommodation: '4-Star Cave Hotel, Goreme, Cappadocia',
      },
      {
        day: 6,
        title: 'Underground City & Ihlara Valley',
        description: 'Morning: Derinkuyu Underground City - an 8-storey Byzantine city carved into volcanic rock, home to 20,000 people. Lunch in the village. Afternoon: Ihlara Valley hike through a 14km canyon with 100+ carved cave churches. Avanos pottery workshop at sunset (make your own Turkish bowl).',
        meals: ['Breakfast', 'Lunch'],
        accommodation: '4-Star Cave Hotel, Goreme, Cappadocia',
      },
      {
        day: 7,
        title: 'Return to Istanbul',
        description: 'Free morning in Cappadocia - last ride through the valley, or a relaxed breakfast on the cave terrace. Afternoon domestic flight back to Istanbul. Optional evening: rooftop bar in Beyoglu for a farewell drink overlooking the Golden Horn.',
        meals: ['Breakfast'],
        accommodation: '4-Star Hotel, Sultanahmet, Istanbul',
      },
      {
        day: 8,
        title: 'Departure',
        description: 'Hotel breakfast and check-out. Transfer to Istanbul Airport (IST) for your return flight to Dubai. Our team confirms ground transport on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Return economy air tickets from Dubai (DXB) to Istanbul (IST)',
      'Domestic flight Istanbul <-> Cappadocia (Nevsehir or Kayseri)',
      '4 nights in Istanbul - 4-star hotel, Sultanahmet area',
      '3 nights in Cappadocia - 4-star cave hotel, Goreme',
      'Daily breakfast',
      'Private Bosphorus dinner cruise with live entertainment',
      'Sunrise hot-air balloon flight over Cappadocia (shared basket, 1 hour)',
      'Guided Old Istanbul tour: Hagia Sophia, Blue Mosque, Topkapi Palace',
      'All airport and intercity transfers',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'Lunches and dinners outside the package',
      'Turkish visa (UAE residents enter visa-free)',
      'Museum entry fees beyond those listed above',
      'Personal shopping and tipping',
      'Travel insurance',
    ],
    hotelExamples: [
      { name: 'Sura Hagia Sophia Hotel', city: 'Istanbul (Sultanahmet)', rating: 4 },
      { name: 'Alzer Hotel', city: 'Istanbul (Sultanahmet)', rating: 4 },
      { name: 'Cave Hotel Saksagan', city: 'Goreme, Cappadocia', rating: 4 },
    ],
    bestTimeToVisit: 'April to June and September to November - mild temperatures and fewer crowds; Cappadocia in December is magical with snow but cold.',
    visaInfo: 'UAE residents do not require a visa for Turkey. Entry is visa-free for up to 90 days using a valid UAE residence permit and passport.',
    flightDuration: 'approx 4hr direct from Dubai (DXB) to Istanbul (IST)',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers',
    whatToBring: [
      'Modest dress for mosque visits (headscarf for women; available at mosque entrance)',
      'Comfortable walking shoes for cobblestone streets and valley hikes',
      'Light jacket for Cappadocia evenings (can be cold even in summer)',
      'Power adapter (Type F, European standard)',
    ],
    faqs: [
      { q: 'Is the hot-air balloon flight safe?', a: 'Cappadocia balloon flights operate with Turkish Civil Aviation Authority-licensed operators. Flights are cancelled if wind speeds exceed safe limits - cancellations are rescheduled to the next morning or a refund is issued.' },
      { q: 'Do UAE residents need a visa for Turkey?', a: 'No. UAE residents can enter Turkey visa-free for stays up to 90 days. You will need your passport and UAE residence permit (Emirates ID).' },
      { q: 'Can we do Istanbul only - without Cappadocia?', a: 'Yes. A 5-night Istanbul-only itinerary is available at a lower price point. The Bosphorus cruise and Grand Bazaar tour are included in both versions.' },
      { q: 'Is the balloon flight in a shared or private basket?', a: 'Standard packages include a shared basket (8-16 passengers). Private baskets for couples or families can be arranged at an additional cost.' },
      { q: 'What if the balloon is cancelled due to weather?', a: 'Licensed operators reschedule you to the next available morning at no charge. If no slot is available before your departure, the balloon cost is refunded.' },
    ],
    cancellationPolicy: '50% refund for cancellations more than 30 days before departure. No refund within 30 days. Balloon flights are non-refundable once flown but will be rescheduled for weather-related cancellations.',
  },

  // -- GEORGIA ---------------------------------------------------------------
  {
    id: 'georgia',
    title: 'Georgia Mountain & City',
    badge: 'Budget Friendly',
    badgeColor: 'green',
    location: 'Tbilisi & Kazbegi, Georgia',
    nights: 5,
    days: 6,
    description: "Kazbegi mountain landscapes, Tbilisi's old town, and Georgian cuisine - Europe's best short break from UAE.",
    priceFromAED: 1399,
    image: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?auto=format&fit=crop&w=800&q=80&fm=webp',
    imageAlt: 'Gergeti Trinity Church beneath Mount Kazbek in the Caucasus',
    categories: ['family', 'short'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Georgia Mountain & City package (5N/6D). Please share details and current pricing.',
    depositAED: 420,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Snow-capped Caucasus mountains in Georgia',
      },
      {
        url: 'https://images.unsplash.com/photo-1565008415402-0782bd03c93e?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tbilisi old town with colorful balconies and the Narikala fortress',
      },
      {
        url: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Gergeti Trinity Church above the clouds with Mount Kazbek behind it',
      },
      {
        url: 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Georgian countryside with rolling hills and medieval tower houses',
      },
    ],
    longDescription: `Georgia is one of those travel secrets that every repeat visitor guards jealously - until they can't stop telling people. Located at the crossroads of Europe and Asia in the South Caucasus, it offers mountain landscapes that rival Switzerland, an ancient capital with carved wooden balconies and sulfur bath houses, some of the world's oldest wine, and food so good that Georgian restaurants have colonised every major city on earth. From the UAE, it's a direct three-hour flight.

This five-night itinerary divides your time between two completely different Georgias. Tbilisi - the capital - is a city of narrow old-town lanes, Orthodox churches, and wine bars where a bottle of natural Rkatsiteli costs less than a soft drink. Two hours north along the Georgian Military Highway, Kazbegi sits at 1,740m with Mount Kazbek (5,047m) as its backdrop. The Gergeti Trinity Church, perched on a hill above the village at 2,170m, is one of the most dramatic viewpoints in the Caucasus.

We handle all transport, hotels, and guided tours - you arrive, we drive. Georgia is excellent for families (safe, cheap, child-friendly), couples looking for a romantic mountain escape, and anyone who values extraordinary food and wine at accessible prices.`,
    highlights: [
      'Kazbegi mountain drive along the Georgian Military Highway',
      'Gergeti Trinity Church hike (2,170m) above the clouds with Kazbek views',
      'Old Tbilisi walking tour: Narikala Fortress, Abanotubani, Metekhi',
      'Mtskheta World Heritage Site - Svetitskhoveli Cathedral (11th century)',
      'Traditional Georgian wine tasting at a family winery',
      'Sulfur bath house experience in Abanotubani (optional)',
      'Khachapuri, khinkali, and churchkhela - a full Georgian food journey',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrive Tbilisi',
        description: 'Arrive at Tbilisi International Airport (TBS). Private transfer to your hotel in the old town (about 20 minutes). Drop bags, walk the Rustaveli Avenue in the evening, and find a table at a wine bar for your first glass of Saperavi. Early night ahead.',
        meals: ['Dinner'],
        accommodation: '4-Star Boutique Hotel, Old Tbilisi',
      },
      {
        day: 2,
        title: 'Tbilisi Old Town & Fortress',
        description: 'Morning: guided Tbilisi walking tour through the old town - wooden carved balconies, the Persian-influenced Abanotubani sulfur bath district, Metekhi Church above the Mtkvari river, and the Narikala Fortress with panoramic city views. Afternoon: Rustaveli Avenue, the National Museum, and the famous Dezerter Bazaar food market. Evening: roof terrace dinner.',
        meals: ['Breakfast'],
        accommodation: '4-Star Boutique Hotel, Old Tbilisi',
      },
      {
        day: 3,
        title: 'Mtskheta & Day Trip',
        description: 'Morning: 30-minute drive to Mtskheta, Georgia\'s ancient capital and UNESCO World Heritage Site. Svetitskhoveli Cathedral (11th century) and Jvari Monastery above the confluence of the two rivers. Afternoon: either return to Tbilisi for the cable car to Narikala, or continue to a family winery in Kakheti for a tasting of natural Georgian wines.',
        meals: ['Breakfast', 'Lunch'],
        accommodation: '4-Star Boutique Hotel, Old Tbilisi',
      },
      {
        day: 4,
        title: 'Kazbegi Mountain Day',
        description: 'Early start for the 2.5-hour drive to Kazbegi via the Georgian Military Highway - a spectacular road through the Greater Caucasus. Stop at the Zhinvali Reservoir, Ananuri Fortress, and the Gudauri ski resort viewpoint. Arrive Kazbegi for lunch. 4x4 drive up to Gergeti Trinity Church (2,170m). Back down before dark, dinner in the village.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: '3-Star Guest House, Kazbegi Village',
      },
      {
        day: 5,
        title: 'Return to Tbilisi & Free Afternoon',
        description: 'Morning at leisure in Kazbegi - optional second Gergeti hike for the fit, or a walk along the Terek river. Drive back to Tbilisi. Afternoon free: Dry Bridge antiques market, Fabrika creative district, or a final khinkali feast. Evening: farewell dinner with Georgian wine.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: '4-Star Boutique Hotel, Old Tbilisi',
      },
      {
        day: 6,
        title: 'Check-out & Departure',
        description: 'Final breakfast at the hotel. Transfer to Tbilisi International Airport (TBS) for your return flight to Dubai. Our team confirms UAE arrival arrangements.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Return economy air tickets from Dubai (DXB) or Sharjah (SHJ)',
      '4 nights in Tbilisi - 4-star boutique hotel, old town',
      '1 night in Kazbegi - 3-star guest house, mountain village',
      'Daily breakfast',
      'Private driver and vehicle throughout (airport, Tbilisi, Mtskheta, Kazbegi)',
      'Licensed Tbilisi old town walking guide (Day 2)',
      'Mtskheta heritage site entry (Day 3)',
      'Traditional Georgian wine tasting session',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'Lunches and dinners outside the package',
      'Sulfur bath house entry (AED 35-100 depending on private room)',
      'Museum entry fees (Tbilisi National Museum etc.)',
      'Travel insurance',
      'Georgian visa (UAE residents enter visa-free)',
    ],
    hotelExamples: [
      { name: 'Fabrika Hostel & Suites', city: 'Tbilisi (Old Town)', rating: 4 },
      { name: 'Old Town Bistro Hotel', city: 'Tbilisi (Old Town)', rating: 4 },
      { name: 'Rooms Hotel Kazbegi', city: 'Kazbegi', rating: 5 },
    ],
    bestTimeToVisit: 'May through October - spring and autumn offer mild weather; June to August is peak summer with green mountains and long daylight hours.',
    visaInfo: 'UAE residents can enter Georgia visa-free for up to 365 days using a valid passport.',
    flightDuration: 'approx 3hr direct from Dubai (DXB) or Sharjah (SHJ)',
    departureCities: ['Dubai (DXB)', 'Sharjah (SHJ)', 'Abu Dhabi (AUH)'],
    groupSize: '2 to 10 travelers; larger groups on request',
    whatToBring: [
      'Sturdy shoes or hiking boots for the Gergeti Church hike',
      'A warm layer for Kazbegi evenings (can be cold even in summer)',
      'Georgian lari (local currency, exchangeable at Tbilisi airport)',
      'Light rain jacket for unpredictable mountain weather',
    ],
    faqs: [
      { q: 'Do UAE residents need a visa for Georgia?', a: 'No. UAE residents can enter Georgia visa-free for up to 365 days using a valid passport. No visa application required.' },
      { q: 'Is Kazbegi included in the package?', a: 'Yes. The package includes a full-day Kazbegi excursion with a private vehicle, the Gergeti Trinity Church drive, and one overnight stay in Kazbegi village.' },
      { q: 'Is this trip suitable for families with children?', a: 'Georgia is one of the most family-friendly destinations in the region. The Gergeti hike can be substituted with a 4x4 vehicle drive for younger children. Food is generally child-friendly.' },
      { q: 'When is the best time to see snow on Kazbeki?', a: 'October to April typically has snow at the higher elevations. Summer (June-August) offers green meadows and the most comfortable hiking conditions.' },
      { q: 'Can you extend the trip to include the Kakheti wine region?', a: 'Yes. An additional 1-2 nights in Kakheti (Georgia\'s wine heartland) can be added to include winery tours, cave city Uplistsikhe, and the David Gareja monastery complex.' },
    ],
    cancellationPolicy: '50% refund for cancellations more than 21 days before departure. No refund within 21 days. Travel date changes accepted up to 14 days before departure subject to availability.',
  },

  // -- AZERBAIJAN ------------------------------------------------------------
  {
    id: 'azerbaijan',
    title: 'Baku City Break',
    badge: 'Short Escape',
    badgeColor: 'navy',
    location: 'Baku, Azerbaijan',
    nights: 4,
    days: 5,
    description: 'The Flame Towers, Icherisheher old city, and Caspian Sea sunsets - a sophisticated short break from Dubai.',
    priceFromAED: 1599,
    image: 'https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?auto=format&fit=crop&w=800&q=80&fm=webp',
    imageAlt: 'Baku Flame Towers and old city walls at twilight',
    categories: ['short', 'family'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Baku City Break (4N/5D). Please share details and current pricing.',
    depositAED: 480,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1601824516843-7faea7cbf4e0?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Baku skyline with the Flame Towers reflected in the water',
      },
      {
        url: 'https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Baku Flame Towers illuminated at night above the old city',
      },
      {
        url: 'https://images.unsplash.com/photo-1547448415-e9f5b28e570d?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Icherisheher old city walls and minarets in Baku',
      },
      {
        url: 'https://images.unsplash.com/photo-1545126220-8fbc3b9d2616?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Baku Boulevard promenade along the Caspian Sea at golden hour',
      },
    ],
    longDescription: `Baku is a city of contrasts that few travelers outside the region have fully discovered - which makes it one of the most rewarding city breaks from the UAE right now. The old walled city of Icherisheher (a UNESCO World Heritage Site) stands intact from the 12th century, with narrow lanes leading past the Maiden Tower and Shirvanshah Palace. Four minutes' walk away, the Flame Towers - three skyscrapers clad in 10,000 LED panels - light up the night sky with fire animations visible from 30km out.

Azerbaijan sits on the Caspian Sea, which gives Baku a genuine waterfront: the 3.5km Baku Boulevard promenade stretches south from the old city through parks, cafes, and the National Flag Square. The city is exceptionally safe, very walkable, and English-speaking to a degree unusual for the region.

This four-night break covers the city's main sights without rushing, with time built in for the Absheron Peninsula (mud volcanoes and a Zoroastrian fire temple that has burned continuously since the 7th century) and the Nizami Street shopping corridor that local families use on weekend evenings. Baku is one of the easiest, most surprising cities you can reach in under two hours from Dubai.`,
    highlights: [
      'Icherisheher old walled city walk - UNESCO World Heritage Site',
      'Flame Towers LED light show from Baku Boulevard at night',
      'Caspian Sea promenade (Baku Boulevard) at sunset',
      'Absheron Peninsula tour: Yanar Dag (eternal flame) and mud volcanoes',
      'Shirvanshah Palace and Maiden Tower with a licensed guide',
      'Nizami Street and Taza Bazar for local shopping and street food',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrive Baku',
        description: 'Arrive at Heydar Aliyev International Airport (GYD). Transfer to your hotel in the city centre (15-25 minutes). Evening walk along Baku Boulevard - the 3.5km Caspian Sea promenade. Dinner at a modern Azerbaijani restaurant with views of the Flame Towers.',
        meals: ['Dinner'],
        accommodation: '4-Star Hotel, Central Baku',
      },
      {
        day: 2,
        title: 'Old City & Flame Towers',
        description: 'Morning: guided walking tour of Icherisheher (Old City) - the walled medieval city with the 12th-century Maiden Tower, Shirvanshah Palace, caravanserais, and the Palace of the Shirvanshahs. Afternoon: Carpet Museum and National Art Museum. Evening: Flame Towers light show from Fountain Square - they perform fire and Azerbaijani pattern animations every 30 minutes.',
        meals: ['Breakfast'],
        accommodation: '4-Star Hotel, Central Baku',
      },
      {
        day: 3,
        title: 'Absheron Peninsula',
        description: 'Half-day tour of the Absheron Peninsula: Ateshgah Fire Temple (Zoroastrian temple built over an eternal natural gas flame, burning since antiquity), Yanar Dag (hillside that has burned continuously for decades), and the grey mud volcano field at Gobustan (UNESCO listed for prehistoric rock carvings). Afternoon free in central Baku.',
        meals: ['Breakfast', 'Lunch'],
        accommodation: '4-Star Hotel, Central Baku',
      },
      {
        day: 4,
        title: 'Shopping, Food & Free Afternoon',
        description: 'Morning: Taza Bazar (covered food market) for Azerbaijani spices, dried fruits, and fresh flatbread. Nizami Street for fashion and local souvenirs. Afternoon: free time - spa, rooftop cafe, or a Caspian Sea boat trip (seasonal, bookable on-site). Evening: farewell dinner at a traditional mugham music restaurant.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: '4-Star Hotel, Central Baku',
      },
      {
        day: 5,
        title: 'Departure',
        description: 'Final breakfast. Check-out and transfer to Heydar Aliyev International Airport (GYD). Return flight to UAE.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Return economy air tickets from Dubai (DXB)',
      '4-night stay in a 4-star hotel, central Baku',
      'Airport transfers (arrival and departure)',
      'Daily breakfast',
      'Licensed Icherisheher old city walking guide (Day 2)',
      'Absheron Peninsula half-day tour with private vehicle (Day 3)',
      'Gobustan rock art site entry',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'Azerbaijani visa (UAE residents may enter visa-free or via ASAN e-Visa)',
      'Lunches and dinners outside the package',
      'Museum entry fees not listed above',
      'Optional Caspian Sea boat trip',
      'Travel insurance',
    ],
    hotelExamples: [
      { name: 'JW Marriott Absheron Baku', city: 'Central Baku', rating: 5 },
      { name: 'Pullman Baku', city: 'Central Baku', rating: 5 },
      { name: 'Hilton Baku', city: 'Central Baku', rating: 5 },
    ],
    bestTimeToVisit: 'April to June and September to November - pleasant temperatures and no summer heat; avoid January and February which can be cold and windy.',
    visaInfo: 'UAE passport holders can enter Azerbaijan visa-free for 30 days. UAE residence permit holders may require an ASAN e-Visa (USD 20, approved in 3 working days). We advise you on your specific situation before travel.',
    flightDuration: 'approx 2hr direct from Dubai (DXB)',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)'],
    groupSize: '2 to 10 travelers',
    whatToBring: [
      'Comfortable walking shoes for Old City cobblestones',
      'Modest dress for religious sites (Ateshgah temple)',
      'Light jacket for cool Caspian Sea evenings',
      'Azerbaijani manat (local currency, exchangeable at airport)',
    ],
    faqs: [
      { q: 'Do UAE residents need a visa for Azerbaijan?', a: 'UAE citizens (passport holders) can enter visa-free for 30 days. UAE residents who are not UAE citizens should apply for an ASAN e-Visa (USD 20, processed in 3 working days). We confirm your requirements when you enquire.' },
      { q: 'Is Baku safe for families?', a: 'Baku is considered one of the safer cities in the region. Icherisheher and the Boulevard are very family-friendly, with wide, well-lit spaces and helpful police presence.' },
      { q: 'Can we extend to include the Sheki region?', a: 'Yes. A 1-2 night extension to Sheki (3 hours from Baku by train) adds medieval fortresses, silk workshops, and mountain villages to the itinerary.' },
      { q: 'How is the food in Baku?', a: 'Excellent. Azerbaijani cuisine shares DNA with Turkish, Persian, and Russian cooking - lamb plov, qutab flatbreads, and pomegranate-dressed salads are everywhere at reasonable prices.' },
      { q: 'Is English spoken in Baku?', a: 'English is widely spoken in hotels, restaurants, and tourist areas. The younger generation in particular communicates easily in English.' },
    ],
    cancellationPolicy: '50% refund for cancellations more than 21 days before departure. No refund within 21 days. Date changes accepted subject to airline availability.',
  },

  // -- MALAYSIA --------------------------------------------------------------
  {
    id: 'malaysia-holiday-packages-from-uae',
    title: 'Selangor, Langkawi & Kuala Lumpur',
    badge: 'Best Value',
    badgeColor: 'teal',
    location: 'Selangor · Langkawi · Kuala Lumpur',
    nights: 7,
    days: 8,
    description: 'Three destinations in one journey — Sunway Lagoon, Langkawi beaches, and the Petronas Towers. 7 nights with domestic flights included.',
    priceFromAED: 3100,
    image: '/images/langkawi-skycab-sky-bridge.webp',
    imageAlt: 'Langkawi SkyCab cable car with aerial view of islands and turquoise sea',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Malaysia package — Selangor, Langkawi & Kuala Lumpur (7N/8D). Please share details and current pricing.',
    depositAED: 930,
    heroGallery: [
      {
        url: '/images/langkawi-skycab-sky-bridge.webp',
        alt: 'Langkawi SkyCab cable car with aerial view of islands and turquoise sea',
      },
      {
        url: '/images/pexels-27-1-418919857-27525026.jpg',
        alt: 'Rooftop infinity pool with Petronas Twin Towers view in Kuala Lumpur',
      },
      {
        url: '/images/caption.png',
        alt: 'Batu Caves golden Murugan statue and colourful steps in Kuala Lumpur',
      },
      {
        url: '/assets/visa/malaysia-petronas-towers-kuala-lumpur.jpg',
        alt: 'Petronas Twin Towers illuminated at night in Kuala Lumpur',
      },
    ],
    longDescription: `Malaysia is one of Southeast Asia's most underrated destinations for UAE travelers — a country where world-class city experiences, tropical island beaches, and family-friendly theme parks sit within a two-hour domestic flight of each other. From the UAE, it's a direct six-hour flight to Kuala Lumpur, and this eight-day package makes full use of the geography.

The journey opens in Selangor, home to Sunway City — a fully integrated township built around Sunway Lagoon, one of Asia's largest water and theme parks with six themed zones. Your hotel, Sunway Pyramid Hotel, connects directly to Sunway Pyramid Mall via a covered walkway. On Day 3, a domestic flight whisks you north to Langkawi, an archipelago of 99 islands off the northwest coast where duty-free status means the most affordable shopping in Malaysia and beaches like Tanjung Rhu and Cenang remain genuinely undiscovered by mass tourism.

Three nights in Langkawi at Bayview Hotel allow time for island-hopping to Pulau Dayang Bunting (Pregnant Maiden Island), a freshwater lake surrounded by jungle, quad biking through the interior, and visits to Langkawi Wildlife Park and Crocodile Farm. The final leg is Kuala Lumpur — the Petronas Twin Towers, KLCC Park and Aquaria, and the Arab Street buzz of Bukit Bintang. A second domestic flight connects Langkawi to KL seamlessly.

The package includes both domestic flights, hotel bookings with daily breakfast, sightseeing tours, a SIM card with unlimited internet, and 24/7 Arabic-speaking customer support. All government taxes are covered.`,
    highlights: [
      'Sunway Lagoon Water Park — one of Asia\'s largest, with 6 themed zones',
      'Langkawi island beaches: Tanjung Rhu and Cenang Beach',
      'Pregnant Maiden Island (Pulau Dayang Bunting) — island-hopping by boat',
      'Langkawi adventure: quad biking, Wildlife Park, and Crocodile Farm',
      'Petronas Twin Towers, KLCC Park, and KLCC Aquaria',
      'Both domestic flights included (Selangor → Langkawi → KL)',
      'SIM card with unlimited internet throughout',
      '24/7 Arabic-speaking customer service',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Selangor',
        description: 'Arrive at Kuala Lumpur International Airport (KLIA). Welcome reception and transfer to Sunway Pyramid Hotel in Selangor. Check in and relax after your flight. The hotel connects directly to Sunway Pyramid Mall for a casual first evening of dining and exploration.',
        meals: [],
        accommodation: 'Sunway Pyramid Hotel, Selangor',
      },
      {
        day: 2,
        title: 'Sunway Lagoon & Sunway Pyramid Mall',
        description: 'Full day at Sunway Lagoon Water Park — Asia\'s multi-award winning theme park with six zones including Water Park, Amusement Park, Wildlife Park, Extreme Park, Scream Park, and Nickelodeon Lost Lagoon. Evening: shopping at Sunway Pyramid Mall, one of Malaysia\'s largest malls with an Egyptian pyramid-inspired design.',
        meals: ['Breakfast'],
        accommodation: 'Sunway Pyramid Hotel, Selangor',
      },
      {
        day: 3,
        title: 'Transfer to Langkawi',
        description: 'Morning check-out and transfer to the airport. Domestic flight to Langkawi International Airport — approximately one hour. Transfer to Bayview Hotel Langkawi and check in. Spend the afternoon relaxing on the beach or exploring the duty-free shops near Cenang Beach.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Hotel Langkawi',
      },
      {
        day: 4,
        title: 'Langkawi Beaches & Pregnant Maiden Island',
        description: 'Full day island exploration. Visit Tanjung Rhu Beach in the north — a long stretch of white sand backed by limestone cliffs with calm, shallow waters. Cenang Beach for lunch and afternoon swimming. Island-hopping boat trip to Pulau Dayang Bunting (Pregnant Maiden Island), home to a freshwater jungle lake surrounded by dense rainforest.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Hotel Langkawi',
      },
      {
        day: 5,
        title: 'Langkawi Adventure Day',
        description: 'Morning quad biking adventure through Langkawi\'s interior — rice paddies, jungle tracks, and rural kampung villages. Visit Langkawi Wildlife Park, one of the largest walk-in aviaries in Malaysia with over 2,500 animals including birds, reptiles, and mammals. Afternoon at Crocodile Farm, home to over 1,000 crocodiles and interactive shows.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Hotel Langkawi',
      },
      {
        day: 6,
        title: 'Transfer to Kuala Lumpur',
        description: 'Morning check-out from Langkawi. Domestic flight to Kuala Lumpur. Transfer to Hotel Royal Signature in the heart of the capital. Check in and relax. Evening walk along Bukit Bintang — KL\'s entertainment district, known as the Arab Street area, packed with Middle Eastern restaurants, shisha cafes, and street food.',
        meals: ['Breakfast'],
        accommodation: 'Hotel Royal Signature, Kuala Lumpur',
      },
      {
        day: 7,
        title: 'Kuala Lumpur City Tour',
        description: 'Full-day Kuala Lumpur sightseeing. Morning: Petronas Twin Towers, the iconic 452-metre twin skyscrapers that defined KL\'s skyline. KLCC Park, the beautifully landscaped urban park at the base of the towers. KLCC Aquaria — one of Southeast Asia\'s most impressive aquariums with a 90-metre underwater tunnel and sand tiger sharks. Afternoon: Bukit Bintang shopping and street food at Alor Street.',
        meals: ['Breakfast'],
        accommodation: 'Hotel Royal Signature, Kuala Lumpur',
      },
      {
        day: 8,
        title: 'Departure',
        description: 'Final breakfast at the hotel. Check-out and transfer to Kuala Lumpur International Airport (KLIA) for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast at all three destinations',
      'Domestic flight: Selangor (KL) → Langkawi',
      'Domestic flight: Langkawi → Kuala Lumpur',
      'Sightseeing tours as per the itinerary',
      'SIM card with unlimited internet throughout Malaysia',
      '24/7 Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Malaysia (available on request)',
      'Lunches and dinners outside the package',
      'Personal shopping and optional activities',
      'Travel insurance',
      'Malaysia visa (if applicable — UAE residents check entry requirements)',
    ],
    hotelExamples: [
      { name: 'Sunway Pyramid Hotel', city: 'Selangor', rating: 4 },
      { name: 'Bayview Hotel Langkawi', city: 'Langkawi', rating: 4 },
      { name: 'Hotel Royal Signature', city: 'Kuala Lumpur', rating: 4 },
    ],
    bestTimeToVisit: 'Malaysia is a year-round destination. December to February is the cooler, drier season in KL and Langkawi. Avoid the East Coast monsoon (November to March) if visiting eastern beaches.',
    visaInfo: 'UAE residents holding certain nationalities can enter Malaysia visa-free for up to 30 days. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Kuala Lumpur (KLIA), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear for Sunway Lagoon and Langkawi beaches',
      'Light, breathable clothing — Malaysia is tropical and humid year-round',
      'Comfortable walking shoes for KL city tour and Langkawi wildlife parks',
      'Reef-safe sunscreen',
    ],
    faqs: [
      { q: 'Are the domestic flights included in the package?', a: 'Yes. Both domestic flights — Selangor (KL) to Langkawi and Langkawi to Kuala Lumpur — are included in the package price.' },
      { q: 'Do UAE residents need a visa for Malaysia?', a: 'Most nationalities holding a UAE residence visa can enter Malaysia visa-free for 30 days. We confirm your specific requirements when you enquire, based on your passport nationality.' },
      { q: 'Is the Petronas Towers entry ticket included?', a: 'Sightseeing tours are included in the package. Entry to the Petronas Towers skybridge and observation deck can be added. Confirm with our team when booking.' },
      { q: 'Is this package suitable for families with young children?', a: 'Absolutely. Sunway Lagoon, Langkawi Wildlife Park, and KLCC Aquaria are all excellent for children. The itinerary is paced to suit families.' },
      { q: 'Can I customize the itinerary or extend the trip?', a: 'Yes. The itinerary can be customized — you can add extra nights in Langkawi, include a Langkawi cable car visit, or extend into Penang. Contact us on WhatsApp to discuss.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- MALAYSIA SELANGOR & KL ------------------------------------------------
  {
    id: 'selangor-kuala-lumpur-holiday-package-from-uae',
    title: 'Selangor & Kuala Lumpur',
    badge: 'Budget Friendly',
    badgeColor: 'green',
    location: 'Selangor · Kuala Lumpur',
    nights: 5,
    days: 6,
    description: 'Sunway Lagoon, the Petronas Twin Towers, KL cultural landmarks — 5 nights in Malaysia with breakfast included from AED 2,200.',
    priceFromAED: 2200,
    image: '/images/caption.png',
    imageAlt: 'Batu Caves golden Murugan statue and colourful steps near Kuala Lumpur',
    categories: ['family', 'short'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Selangor & Kuala Lumpur 5 Nights package. Please share details and current pricing.',
    depositAED: 660,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Petronas Twin Towers and KLCC skyline at night in Kuala Lumpur',
      },
      {
        url: '/images/caption.png',
        alt: 'Batu Caves golden Murugan statue and colourful steps near Kuala Lumpur',
      },
      {
        url: '/images/pexels-27-1-418919857-27525026.jpg',
        alt: 'Rooftop infinity pool with Petronas Twin Towers view in Kuala Lumpur',
      },
      {
        url: '/assets/visa/malaysia-petronas-towers-kuala-lumpur.jpg',
        alt: 'Petronas Twin Towers illuminated at night in Kuala Lumpur',
      },
    ],
    longDescription: `Malaysia's twin cities of Selangor and Kuala Lumpur offer two completely different experiences within a single short holiday — and this five-night package from UAE delivers both without rushing either.

Selangor is home to Sunway City, built around Sunway Lagoon Water Park — one of Asia's most popular theme parks with six zones covering water rides, amusement rides, wildlife, and extreme sports. Your hotel, Sunway Pyramid Hotel, connects directly to Sunway Pyramid Mall, one of Malaysia's largest shopping destinations with an Egyptian pyramid-inspired design and over 900 outlets.

On Day 3, a short road transfer moves you into the capital. Three nights at Hotel Royal Signature put you in the heart of Kuala Lumpur for the Petronas Twin Towers, KLCC Aquaria, and the cultural district. Day 5 is dedicated to KL's heritage layer — the colonial Sultan Abdul Samad Building, Chinatown's Petaling Street market, and Central Market where local art, batik fabric, and Malaysian crafts fill a beautifully restored art deco building.

The package includes hotel with daily breakfast, all sightseeing tours, a SIM card with unlimited internet, 24/7 customer service, and all government taxes. No domestic flights needed — Selangor and KL are 30 minutes apart by road.`,
    highlights: [
      'Sunway Lagoon Water Park — 6 themed zones, one of Asia\'s top theme parks',
      'Petronas Twin Towers, KLCC Park, and KLCC Aquaria in Kuala Lumpur',
      'Cultural Day: Sultan Abdul Samad Building, Chinatown & Central Market',
      'Sunway Pyramid Mall — 900+ outlets, Egyptian pyramid design',
      'Bukit Bintang street tour — KL\'s entertainment and dining hub',
      'SIM card with unlimited internet throughout',
      'No domestic flights needed — Selangor to KL is 30 minutes',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Selangor',
        description: 'Arrive at Kuala Lumpur International Airport (KLIA). Our representative meets you at arrivals and transfers you to Sunway Pyramid Hotel in Selangor. Check in, freshen up, and relax. The hotel connects directly to Sunway Pyramid Mall — perfect for a casual first evening of dining and exploration.',
        meals: [],
        accommodation: 'Sunway Pyramid Hotel, Selangor — Double Deluxe Room',
      },
      {
        day: 2,
        title: 'Sunway Lagoon & Sunway Pyramid Mall',
        description: 'Full day at Sunway Lagoon Water Park — Asia\'s multi-award winning theme park featuring six zones: Water Park, Amusement Park, Wildlife Park, Extreme Park, Scream Park, and Nickelodeon Lost Lagoon. Thrills, slides, and live shows for the whole day. Evening: shopping and dining at Sunway Pyramid Mall, one of Malaysia\'s largest and most iconic malls.',
        meals: ['Breakfast'],
        accommodation: 'Sunway Pyramid Hotel, Selangor — Double Deluxe Room',
      },
      {
        day: 3,
        title: 'Transfer to Kuala Lumpur',
        description: 'Morning at leisure. After checkout, transfer to Hotel Royal Signature in Kuala Lumpur — approximately 30 minutes by road. Check in and rest. Afternoon free to explore the surroundings at your own pace. Evening stroll along Bukit Bintang, KL\'s main entertainment strip with restaurants, cafes, and night markets.',
        meals: ['Breakfast'],
        accommodation: 'Hotel Royal Signature, Kuala Lumpur — Double Deluxe Room',
      },
      {
        day: 4,
        title: 'Kuala Lumpur Landmarks',
        description: 'Full-day Kuala Lumpur sightseeing. Morning: Petronas Twin Towers — the iconic 452-metre twin skyscrapers and the defining image of modern Malaysia. KLCC Park, the beautifully landscaped urban park at the base of the towers with fountains and jogging paths. KLCC Aquaria — one of Southeast Asia\'s finest aquariums with a 90-metre underwater walkthrough tunnel, sand tiger sharks, and thousands of marine species. Afternoon: Bukit Bintang Street tour — KL\'s entertainment and shopping district packed with restaurants, street food, and local life.',
        meals: ['Breakfast'],
        accommodation: 'Hotel Royal Signature, Kuala Lumpur — Double Deluxe Room',
      },
      {
        day: 5,
        title: 'Cultural Tour',
        description: 'A day dedicated to Kuala Lumpur\'s heritage and culture. Visit the Sultan Abdul Samad Building — a stunning Moorish-style colonial building facing Dataran Merdeka (Independence Square), the site where Malaysia declared independence in 1957. Explore Chinatown on Petaling Street — a covered night-market street alive with food stalls, shops, and local energy day and night. Finish at Central Market, a heritage art deco building housing Malaysian arts, crafts, batik fabrics, and cultural souvenirs.',
        meals: ['Breakfast'],
        accommodation: 'Hotel Royal Signature, Kuala Lumpur — Double Deluxe Room',
      },
      {
        day: 6,
        title: 'Departure',
        description: 'Final breakfast at the hotel. Check out and transfer to Kuala Lumpur International Airport (KLIA) for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel reservations with daily breakfast at both destinations',
      '2 nights Sunway Pyramid Hotel, Selangor (Double Deluxe Room)',
      '3 nights Hotel Royal Signature, Kuala Lumpur (Double Deluxe Room)',
      'Tours as per the itinerary',
      'SIM card with unlimited internet throughout Malaysia',
      '24/7 customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Malaysia (available on request)',
      'Lunches and dinners outside the package',
      'Personal shopping and optional activities',
      'Travel insurance',
      'Malaysia visa (if applicable — check entry requirements based on your nationality)',
    ],
    hotelExamples: [
      { name: 'Sunway Pyramid Hotel', city: 'Selangor', rating: 4 },
      { name: 'Hotel Royal Signature', city: 'Kuala Lumpur', rating: 4 },
    ],
    bestTimeToVisit: 'Malaysia is a year-round destination. December to February is the cooler, drier season. The country experiences occasional afternoon showers year-round — lightweight rain gear is recommended.',
    visaInfo: 'Many nationalities including GCC residents can enter Malaysia visa-free for up to 30 days. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Kuala Lumpur (KLIA), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear and comfortable shoes for Sunway Lagoon',
      'Light, breathable clothing — Malaysia is tropical and humid',
      'Comfortable walking shoes for the cultural day',
      'Small daypack for sightseeing days',
    ],
    faqs: [
      { q: 'Are the hotel rooms Double Deluxe?', a: 'Yes. Both hotels — Sunway Pyramid Hotel in Selangor and Hotel Royal Signature in Kuala Lumpur — are booked as Double Deluxe Rooms. Upgrades are available on request.' },
      { q: 'Is there a domestic flight in this package?', a: 'No domestic flight is needed. Selangor and Kuala Lumpur are approximately 30 minutes apart by road. The transfer is included in the package.' },
      { q: 'Do UAE residents need a visa for Malaysia?', a: 'Most nationalities residing in the UAE can enter Malaysia visa-free for 30 days. We confirm your specific requirements when you enquire, based on your passport nationality.' },
      { q: 'Is Sunway Lagoon entry included?', a: 'Yes. Tours mentioned in the itinerary are included in the package. The Sunway Lagoon visit on Day 2 is part of the included sightseeing.' },
      { q: 'Can this package be customized or extended?', a: 'Yes. You can add extra nights, include Genting Highlands, Batu Caves, or Putrajaya, or extend into Langkawi. Contact us on WhatsApp to discuss.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per hotel and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- MALAYSIA 9 NIGHTS -----------------------------------------------------
  {
    id: 'selangor-langkawi-kuala-lumpur-9-nights-malaysia',
    title: 'Selangor, Langkawi & Kuala Lumpur — 9 Nights',
    badge: 'Best Value',
    badgeColor: 'teal',
    location: 'Selangor · Langkawi · Kuala Lumpur',
    nights: 9,
    days: 10,
    description: 'The ultimate Malaysia experience — 9 nights across Selangor, Langkawi island, and Kuala Lumpur with domestic flights and breakfast included.',
    priceFromAED: 3600,
    image: '/images/langkawi-skycab-sky-bridge.webp',
    imageAlt: 'Langkawi SkyCab cable car with aerial view of islands and turquoise sea',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Selangor, Langkawi & Kuala Lumpur 9 Nights package. Please share details and current pricing.',
    depositAED: 1080,
    heroGallery: [
      {
        url: '/images/langkawi-skycab-sky-bridge.webp',
        alt: 'Langkawi SkyCab cable car with aerial panoramic view of islands and turquoise sea',
      },
      {
        url: '/images/pexels-27-1-418919857-27525026.jpg',
        alt: 'Rooftop infinity pool with Petronas Twin Towers view in Kuala Lumpur',
      },
      {
        url: '/images/caption.png',
        alt: 'Batu Caves golden Murugan statue and colourful steps near Kuala Lumpur',
      },
      {
        url: '/assets/visa/malaysia-petronas-towers-kuala-lumpur.jpg',
        alt: 'Petronas Twin Towers illuminated at night in Kuala Lumpur',
      },
    ],
    longDescription: `This 10-day, 9-night Malaysia package is the most complete way to experience what the country offers — a theme park city, a tropical island archipelago, and one of Southeast Asia's great capitals, all in a single well-paced journey.

The trip opens in Selangor with Sunway Lagoon, Asia's multi-award winning theme park, and Sunway Pyramid Mall. A domestic flight on Day 3 takes you north to Langkawi — four full days on Malaysia's most celebrated island. With duty-free status, pristine beaches at Tanjung Rhu and Cenang, a boat cruise to Pregnant Maiden Island (Pulau Dayang Bunting), Crocodile Farm, Wildlife Park, and quad biking through the interior, Langkawi rewards those who stay long enough to slow down.

The final chapter is Kuala Lumpur. Three nights at Royal Signature Hotel cover the Petronas Twin Towers, KLCC Park, KLCC Aquaria, Bukit Bintang (Arab Street), and a dedicated cultural day through Chinatown's Petaling Street, Central Market, and Sultan's Palace. Both domestic flights — Selangor to Langkawi and Langkawi to KL — are included, along with daily breakfast, sightseeing tours, SIM card with unlimited data, 24/7 Arabic-speaking customer service, and all government taxes.`,
    highlights: [
      'Sunway Lagoon Theme Park & Sunway Pyramid Mall (Selangor)',
      '4 nights in Langkawi — beaches, island cruise & adventure activities',
      'Tanjung Rhu & Cenang Beach with cruise to Pregnant Maiden Island',
      'Quad biking, Crocodile Farm & Wildlife Park in Langkawi',
      'Petronas Twin Towers, KLCC Park & KLCC Aquaria (Kuala Lumpur)',
      'Cultural tour: Chinatown, Central Market & Sultan\'s Palace',
      'Both domestic flights included (Selangor → Langkawi → KL)',
      '24/7 Arabic-speaking customer service throughout',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Selangor',
        description: 'Arrive at Kuala Lumpur International Airport (KLIA). Transfer to Sunway Pyramid Hotel in Selangor. Check in and relax. The hotel connects directly to Sunway Pyramid Mall — ideal for a first-evening stroll and dinner.',
        meals: [],
        accommodation: 'Sunway Pyramid Hotel, Selangor',
      },
      {
        day: 2,
        title: 'Sunway Lagoon & Sunway Pyramid Mall',
        description: 'Full day at Sunway Lagoon Theme Park — six zones including Water Park, Amusement Park, Wildlife Park, Extreme Park, Scream Park, and Nickelodeon Lost Lagoon. Evening: shopping and dining at Sunway Pyramid Mall.',
        meals: ['Breakfast'],
        accommodation: 'Sunway Pyramid Hotel, Selangor',
      },
      {
        day: 3,
        title: 'Transfer to Langkawi',
        description: 'Morning checkout. Domestic flight from Kuala Lumpur to Langkawi International Airport. Transfer to Bayview Langkawi Hotel and check in. Afternoon to explore Cenang Beach or the duty-free shops at your leisure.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Langkawi Hotel, Langkawi',
      },
      {
        day: 4,
        title: 'Langkawi Beaches',
        description: 'Beach day covering Langkawi\'s finest shores. Tanjung Rhu Beach in the north — a long white sand stretch backed by limestone karst cliffs with calm, shallow water ideal for families. Cenang Beach in the afternoon for swimming, watersports, and beachfront dining.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Langkawi Hotel, Langkawi',
      },
      {
        day: 5,
        title: 'Pregnant Maiden Island Cruise',
        description: 'Island-hopping boat cruise to Pulau Dayang Bunting (Pregnant Maiden Island) — a freshwater lake surrounded by dense jungle inside a limestone island. Legend holds the lake brings fertility. Return via other small islands of the Langkawi archipelago.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Langkawi Hotel, Langkawi',
      },
      {
        day: 6,
        title: 'Langkawi Adventure Day',
        description: 'Quad biking through Langkawi\'s rural interior — rice paddies and kampung villages. Visit Langkawi Wildlife Park with over 2,500 animals including exotic birds, reptiles, and mammals. Afternoon at Crocodile Farm with over 1,000 crocodiles and interactive shows.',
        meals: ['Breakfast'],
        accommodation: 'Bayview Langkawi Hotel, Langkawi',
      },
      {
        day: 7,
        title: 'Transfer to Kuala Lumpur',
        description: 'Morning checkout from Langkawi. Domestic flight to Kuala Lumpur. Transfer to Royal Signature Hotel in central KL. Check in and relax. Evening on Bukit Bintang (Arab Street) — KL\'s main entertainment district with Middle Eastern restaurants, shisha cafes, and street food.',
        meals: ['Breakfast'],
        accommodation: 'Royal Signature Hotel, Kuala Lumpur',
      },
      {
        day: 8,
        title: 'Kuala Lumpur Landmarks',
        description: 'Full-day KL sightseeing. Petronas Twin Towers — the iconic 452-metre twin skyscrapers. KLCC Park at the base of the towers. KLCC Aquaria — 90-metre underwater tunnel with sand tiger sharks and thousands of marine species. Bukit Bintang street tour in the evening.',
        meals: ['Breakfast'],
        accommodation: 'Royal Signature Hotel, Kuala Lumpur',
      },
      {
        day: 9,
        title: 'Cultural Tour',
        description: 'KL\'s heritage and culture. Chinatown on Petaling Street — a covered market street alive with food stalls and local energy. Central Market — a heritage art deco building housing Malaysian arts, crafts, batik, and souvenirs. Sultan\'s Palace (Istana Negara) — the official residence of the King of Malaysia, impressive Moorish architecture set in landscaped grounds.',
        meals: ['Breakfast'],
        accommodation: 'Royal Signature Hotel, Kuala Lumpur',
      },
      {
        day: 10,
        title: 'Departure',
        description: 'Final breakfast at the hotel. Check out and transfer to Kuala Lumpur International Airport (KLIA) for your return flight to the UAE.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel reservations with daily breakfast at all three destinations',
      'Domestic flight: Selangor (KL) → Langkawi',
      'Domestic flight: Langkawi → Kuala Lumpur',
      'Sightseeing tours as per itinerary',
      'SIM card with unlimited data throughout Malaysia',
      '24/7 Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Malaysia (available on request)',
      'Lunches and dinners outside the package',
      'Personal shopping and optional activities',
      'Travel insurance',
      'Malaysia visa (if applicable — check entry requirements based on your nationality)',
    ],
    hotelExamples: [
      { name: 'Sunway Pyramid Hotel', city: 'Selangor', rating: 4 },
      { name: 'Bayview Langkawi Hotel', city: 'Langkawi', rating: 4 },
      { name: 'Royal Signature Hotel', city: 'Kuala Lumpur', rating: 4 },
    ],
    bestTimeToVisit: 'Malaysia is a year-round destination. December to February is cooler and drier. Langkawi is best from November to April when seas are calm. Avoid the monsoon season on the east coast (November to March).',
    visaInfo: 'Most nationalities residing in the UAE can enter Malaysia visa-free for up to 30 days. We confirm your specific requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Kuala Lumpur (KLIA), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear for Sunway Lagoon and Langkawi beaches',
      'Light, breathable clothing — Malaysia is tropical and humid year-round',
      'Comfortable walking shoes for KL cultural day',
      'Reef-safe sunscreen for Langkawi',
    ],
    faqs: [
      { q: 'Are both domestic flights included?', a: 'Yes. The domestic flight from Selangor (KL) to Langkawi and the domestic flight from Langkawi to Kuala Lumpur are both included in the package price.' },
      { q: 'How many nights are in Langkawi?', a: '4 nights in Langkawi at Bayview Langkawi Hotel, giving you enough time to cover the beaches, island cruise, and adventure activities without rushing.' },
      { q: 'Do UAE residents need a visa for Malaysia?', a: 'Most nationalities holding a UAE residence visa can enter Malaysia visa-free for 30 days. We confirm your specific requirements when you enquire based on your passport nationality.' },
      { q: 'Is this package suitable for families with children?', a: 'Yes. Sunway Lagoon, Langkawi Wildlife Park, Pregnant Maiden Island, and KLCC Aquaria are all excellent for children. The pacing is relaxed with no early morning starts.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. Extra nights, Genting Highlands, Batu Caves, Langkawi cable car, or Penang can be added. Contact us on WhatsApp to discuss options.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- INDONESIA (JAKARTA, PUNCAK, BALI) -------------------------------------
  {
    id: 'jakarta-puncak-bali-holiday-package-from-uae',
    title: 'Jakarta, Puncak & Bali',
    badge: 'Family Favourite',
    badgeColor: 'teal',
    location: 'Jakarta · Puncak · Bali',
    nights: 7,
    days: 8,
    description: 'Three sides of Indonesia — the cool hills of Puncak, the beaches and temples of Bali, and the capital Jakarta. 7 nights with domestic flights and a private Arabic-speaking driver.',
    priceFromAED: 2750,
    image: '/images/indonesia-jakarta-puncak-bali.webp',
    imageAlt: 'Balinese people in traditional dress carrying offerings on a boat before a tiered temple by a lake in Bali, Indonesia',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Jakarta, Puncak & Bali package (7N/8D). Please share details and current pricing.',
    depositAED: 825,
    heroGallery: [
      {
        url: '/images/indonesia-jakarta-puncak-bali.webp',
        alt: 'Balinese people in traditional dress carrying offerings on a boat before a tiered temple by a lake in Bali, Indonesia',
      },
      {
        url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tegallalang rice terraces in Bali with morning mist',
      },
      {
        url: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Uluwatu clifftop temple above the ocean in Bali',
      },
      {
        url: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Private pool villa surrounded by tropical garden in Bali',
      },
    ],
    longDescription: `Indonesia in one trip: this eight-day package from UAE pairs the cool mountain resort town of Puncak, the beaches and culture of Bali, and a night in the capital Jakarta — connected by domestic flights and a private car with an Arabic-speaking driver throughout.

The journey opens in Puncak, a green highland retreat in the hills south of Jakarta known for its tea plantations and cooler climate. You'll spend two nights at the Pesona Alam Resort, visit Taman Safari (the drive-through safari park), and see the striking Colored Lake at the tea garden.

A domestic flight then carries you to Bali for four nights at the Aston Bali Hotel — the heart of the trip. Days are spent on the water at Tanjung Benoa (watersports, Turtle Island, the Water Blow at Nusa Dua) and on the white sand of Pandawa Beach, followed by a full cultural day through a silver and gold workshop, a coffee plantation, the bird park and butterfly garden, and the Tegenungan Waterfall. A free day lets you shop, relax by the pool, or add an optional excursion.

The trip closes with a domestic flight back to Jakarta for a night at the Novotel Cikini before your departure. Daily breakfast, a free SIM card, and private transfers are included from start to finish.`,
    highlights: [
      'Two nights in the cool highlands of Puncak at Pesona Alam Resort',
      'Taman Safari drive-through park & the Colored Lake tea garden',
      'Four nights in Bali at the Aston Bali Hotel',
      'Watersports, Turtle Island, Water Blow & Pandawa Beach',
      'Cultural day: silver/gold workshop, coffee plantation, bird & butterfly park, Tegenungan Waterfall',
      'Domestic flights included (Jakarta – Bali – Jakarta)',
      'Private car with an Arabic-speaking driver throughout',
      'Daily breakfast + free SIM card',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Jakarta → Puncak',
        description: 'Arrive at Jakarta (Soekarno-Hatta International Airport). Meet your private Arabic-speaking driver and transfer directly to Puncak in the cool highlands south of the city. Check in at Pesona Alam Resort and relax in the mountain air.',
        meals: [],
        accommodation: 'Pesona Alam Resort, Puncak',
      },
      {
        day: 2,
        title: 'Safari Park & Colored Lake',
        description: 'Full day in Puncak. Visit Taman Safari — the popular drive-through safari park where animals roam freely — then head to the tea garden to see the striking Colored Lake set among the plantations. Evening free at the resort.',
        meals: ['Breakfast'],
        accommodation: 'Pesona Alam Resort, Puncak',
      },
      {
        day: 3,
        title: 'Fly to Bali',
        description: 'Transfer to the airport for your domestic flight to Bali (Ngurah Rai International Airport). On arrival, your driver transfers you to the Aston Bali Hotel. Rest and settle in for the Bali leg of your trip.',
        meals: ['Breakfast'],
        accommodation: 'Aston Bali Hotel',
      },
      {
        day: 4,
        title: 'Watersports & Southern Beaches',
        description: 'A day on the water: the Tanjung Benoa watersports area, a visit to Turtle Island, the dramatic Water Blow at Nusa Dua where waves crash through the rocks, and the white sand of Pandawa Beach tucked beneath limestone cliffs.',
        meals: ['Breakfast'],
        accommodation: 'Aston Bali Hotel',
      },
      {
        day: 5,
        title: 'Bali Culture & Nature Tour',
        description: 'Explore Bali\'s craft and nature: a traditional silver and gold workshop, a Balinese coffee plantation with tastings, the bird park and butterfly garden, and the beautiful Tegenungan Waterfall for photos and a cool break.',
        meals: ['Breakfast'],
        accommodation: 'Aston Bali Hotel',
      },
      {
        day: 6,
        title: 'Free Day in Bali',
        description: 'A free day at leisure. Relax by the hotel pool, shop for souvenirs, enjoy the beach, or add an optional excursion such as Ubud, the Tegallalang rice terraces, or a sunset temple visit (arranged on request).',
        meals: ['Breakfast'],
        accommodation: 'Aston Bali Hotel',
      },
      {
        day: 7,
        title: 'Return to Jakarta',
        description: 'Morning at leisure, then transfer to the airport for your domestic flight back to Jakarta. On arrival, your driver transfers you to the Novotel Cikini in central Jakarta. Evening free to rest or explore the surroundings.',
        meals: ['Breakfast'],
        accommodation: 'Novotel Cikini, Jakarta',
      },
      {
        day: 8,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and transfer to Jakarta airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel reservations with daily breakfast at all three destinations',
      '2 nights Pesona Alam Resort, Puncak',
      '4 nights Aston Bali Hotel, Bali',
      '1 night Novotel Cikini, Jakarta',
      'Domestic flight tickets: Jakarta – Bali – Jakarta',
      'Private car with an Arabic-speaking driver throughout the trip',
      'Sightseeing and tours as per the itinerary',
      'Free SIM card',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Indonesia (available on request)',
      'Lunches and dinners outside the package',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Indonesia visa on arrival (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Pesona Alam Resort', city: 'Puncak', rating: 4 },
      { name: 'Aston Bali Hotel', city: 'Bali', rating: 4 },
      { name: 'Novotel Cikini', city: 'Jakarta', rating: 4 },
    ],
    bestTimeToVisit: 'Indonesia is a year-round destination. Bali is driest and sunniest from April to October; Puncak is cool and green all year, with occasional afternoon showers.',
    visaInfo: 'Many nationalities can enter Indonesia via visa on arrival (around USD 35) for up to 30 days. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 8-9hr from Dubai (DXB) to Jakarta (CGK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear for Bali beaches and watersports',
      'A light jacket or layer for cool Puncak evenings',
      'Light, breathable clothing for the tropical lowlands',
      'Reef-safe sunscreen and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Are international flights included in this package?', a: 'No. The package price covers the domestic flights Jakarta–Bali–Jakarta, hotels, transfers, and tours. International flights from the UAE can be arranged separately on request.' },
      { q: 'Is the driver Arabic-speaking?', a: 'Yes. A private car with an Arabic-speaking driver accompanies you throughout the trip in both Puncak/Jakarta and Bali.' },
      { q: 'How many nights are in Bali?', a: 'Four nights at the Aston Bali Hotel, plus two nights in Puncak and one night in Jakarta — seven nights in total over eight days.' },
      { q: 'Do UAE residents need a visa for Indonesia?', a: 'Most nationalities can enter Indonesia on a visa on arrival (around USD 35) for stays up to 30 days. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can add Ubud, the rice terraces, extra Bali nights, or upgrade hotels. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- INDONESIA 9 NIGHTS (JAKARTA, PUNCAK, BALI) ----------------------------
  {
    id: 'jakarta-puncak-bali-9-nights-holiday-package-from-uae',
    title: 'Jakarta, Puncak & Bali — 9 Nights',
    badge: 'Best Value',
    badgeColor: 'teal',
    location: 'Jakarta · Puncak · Bali',
    nights: 9,
    days: 10,
    description: 'The complete Indonesia journey — the hills of Puncak, six full nights in Bali, and Jakarta. 10 days with domestic flights and a private Arabic-speaking driver.',
    priceFromAED: 3360,
    image: '/images/indonesia-bali-family-beach.webp',
    imageAlt: 'A family walking together along a white sand beach with turquoise water and cliffs in Bali, Indonesia',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Jakarta, Puncak & Bali 9 Nights package (9N/10D). Please share details and current pricing.',
    depositAED: 1008,
    heroGallery: [
      {
        url: '/images/indonesia-bali-family-beach.webp',
        alt: 'A family walking together along a white sand beach with turquoise water and cliffs in Bali, Indonesia',
      },
      {
        url: '/images/indonesia-jakarta-puncak-bali.webp',
        alt: 'Balinese people in traditional dress carrying offerings on a boat before a tiered temple by a lake in Bali, Indonesia',
      },
      {
        url: 'https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tanah Lot sea temple on a rock at sunset in Bali, Indonesia',
      },
      {
        url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tegallalang rice terraces in Bali with morning mist',
      },
      {
        url: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Uluwatu clifftop temple above the ocean in Bali',
      },
    ],
    longDescription: `The definitive Indonesia trip from UAE: ten days across the cool highlands of Puncak, six full nights in Bali, and the capital Jakarta — all connected by domestic flights and a private car with an Arabic-speaking driver throughout.

The journey begins in Puncak, a green mountain retreat south of Jakarta, with two nights at the Pesona Alam Resort, a visit to Taman Safari, and the striking Lake of Colors in the tea garden. A domestic flight then carries you to Bali for the heart of the trip — six nights at the Aston.

With six nights in Bali there is time to go far beyond the highlights: watersports and Pandawa Beach at Tanjung Benoa, river rowing and horseback riding on Saba Beach, a boat trip to Nusa Penida island and Blue Ocean Beach, ATV quad biking, the elephant park in Ubud and a chocolate factory, plus the classic culture day of silver and gold workshops, a coffee plantation, the bird park and butterfly garden, and the Tegenungan Waterfall.

The trip closes with a domestic flight back to Jakarta for a night at the Novotel Cikini before your departure. Daily breakfast, a free SIM card, and all private transfers are included throughout.`,
    highlights: [
      'Six full nights in Bali at the Aston — time for the deeper experiences',
      'Two nights in the cool highlands of Puncak (Taman Safari + Lake of Colors)',
      'Nusa Penida island day trip & Blue Ocean Beach',
      'Watersports, Turtle Island, Water Blow & Pandawa Beach',
      'River rowing, horseback riding on Saba Beach & ATV quad biking',
      'Ubud elephant park, chocolate factory & Tegenungan Waterfall',
      'Domestic flights included (Jakarta – Bali – Jakarta)',
      'Private car with an Arabic-speaking driver + free SIM card',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Jakarta → Puncak',
        description: 'Arrive at Jakarta (Soekarno-Hatta International Airport). Meet your private Arabic-speaking driver and transfer directly to Puncak in the cool highlands. Check in at Pesona Alam Resort and relax.',
        meals: [],
        accommodation: 'Pesona Alam Resort, Puncak',
      },
      {
        day: 2,
        title: 'Safari Zoo & Lake of Colors',
        description: 'Full day in Puncak: visit Taman Safari, the popular drive-through safari park, then the tea garden to see the striking Lake of Colors set among the plantations.',
        meals: ['Breakfast'],
        accommodation: 'Pesona Alam Resort, Puncak',
      },
      {
        day: 3,
        title: 'Fly to Bali',
        description: 'Transfer to the airport for your domestic flight to Bali (Ngurah Rai International Airport). Transfer to the Aston and rest, settling in for the Bali leg of your trip.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 4,
        title: 'Watersports & Southern Beaches',
        description: 'A day on the water: the Tanjung Benoa watersports area, a visit to Turtle Island, the dramatic Water Blow at Nusa Dua, and the white sand of Pandawa Beach beneath limestone cliffs.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 5,
        title: 'River Rowing & Saba Beach',
        description: 'Morning river rowing/rafting through Bali\'s green interior, followed by horseback riding along the black sand of Saba Beach — a relaxed, scenic day away from the crowds.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 6,
        title: 'Nusa Penida Island',
        description: 'Full-day boat trip to Nusa Penida island — dramatic cliff viewpoints, crystal-clear water, and Blue Ocean Beach. One of the most spectacular days in all of Bali.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 7,
        title: 'ATV, Ubud Elephant Park & Chocolate Factory',
        description: 'Adventure and Ubud: ATV quad biking through jungle and rice-field tracks, a visit to the elephant park in Ubud, and a stop at a Balinese chocolate factory for tastings.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 8,
        title: 'Bali Culture & Nature Tour',
        description: 'Bali\'s craft and nature: silver and gold workshops, a coffee plantation with tastings, the bird park and butterfly garden, and the beautiful Tegenungan Waterfall.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 9,
        title: 'Return to Jakarta',
        description: 'Morning at leisure, then transfer to the airport for your domestic flight back to Jakarta. Transfer to the Novotel Cikini in central Jakarta. Evening free to rest or explore.',
        meals: ['Breakfast'],
        accommodation: 'Novotel Cikini, Jakarta',
      },
      {
        day: 10,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and transfer to Jakarta airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel reservations with daily breakfast at all three destinations',
      '2 nights Pesona Alam Resort, Puncak',
      '6 nights Aston, Bali',
      '1 night Novotel Cikini, Jakarta',
      'Domestic flight tickets: Jakarta – Bali – Jakarta',
      'Private car with an Arabic-speaking driver throughout the trip',
      'Sightseeing and tours as per the itinerary',
      'Free SIM card',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Indonesia (available on request)',
      'Lunches and dinners outside the package',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Indonesia visa on arrival (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Pesona Alam Resort', city: 'Puncak', rating: 4 },
      { name: 'Aston', city: 'Bali', rating: 4 },
      { name: 'Novotel Cikini', city: 'Jakarta', rating: 4 },
    ],
    bestTimeToVisit: 'Indonesia is a year-round destination. Bali is driest and sunniest from April to October; Puncak is cool and green all year, with occasional afternoon showers.',
    visaInfo: 'Many nationalities can enter Indonesia via visa on arrival (around USD 35) for up to 30 days. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 8-9hr from Dubai (DXB) to Jakarta (CGK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear for Bali beaches, watersports and Nusa Penida',
      'A light jacket or layer for cool Puncak evenings',
      'Light, breathable clothing for the tropical lowlands',
      'Reef-safe sunscreen and comfortable walking/adventure shoes',
    ],
    faqs: [
      { q: 'How many nights are in Bali?', a: 'Six nights at the Aston in Bali, plus two nights in Puncak and one night in Jakarta — nine nights in total over ten days. The extra Bali time makes room for Nusa Penida, ATV, Ubud and more.' },
      { q: 'Are international flights included?', a: 'No. The package covers the domestic flights Jakarta–Bali–Jakarta, hotels, transfers, and tours. International flights from the UAE can be arranged separately on request.' },
      { q: 'Is the driver Arabic-speaking?', a: 'Yes. A private car with an Arabic-speaking driver accompanies you throughout the trip in both Puncak/Jakarta and Bali.' },
      { q: 'Do UAE residents need a visa for Indonesia?', a: 'Most nationalities can enter Indonesia on a visa on arrival (around USD 35) for stays up to 30 days. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can adjust activities, upgrade hotels, or add nights. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- INDONESIA 8 NIGHTS (JAKARTA, PUNCAK, BALI) ----------------------------
  {
    id: 'jakarta-puncak-bali-8-nights-holiday-package-from-uae',
    title: 'Jakarta, Puncak & Bali — 8 Nights',
    badge: 'Family Favourite',
    badgeColor: 'teal',
    location: 'Jakarta · Puncak · Bali',
    nights: 8,
    days: 9,
    description: 'Puncak\'s cool hills, five nights in Bali, and Jakarta — with the Handara Gate, watersports and Ubud adventures. 9 days, domestic flights and a private Arabic-speaking driver included.',
    priceFromAED: 3200,
    image: '/images/indonesia-nusa-penida-kelingking-card.webp',
    imageAlt: 'Kelingking Beach cliff and turquoise bay on Nusa Penida, Bali, Indonesia',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Jakarta, Puncak & Bali 8 Nights package (8N/9D). Please share details and current pricing.',
    depositAED: 960,
    heroGallery: [
      {
        url: '/images/indonesia-nusa-penida-kelingking.webp',
        alt: 'Kelingking Beach cliff and turquoise bay on Nusa Penida, Bali, Indonesia',
      },
      {
        url: '/images/indonesia-bali-family-beach.webp',
        alt: 'A family walking together along a white sand beach with turquoise water in Bali, Indonesia',
      },
      {
        url: 'https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tanah Lot sea temple on a rock at sunset in Bali, Indonesia',
      },
      {
        url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Tegallalang rice terraces in Bali with morning mist',
      },
    ],
    longDescription: `A well-balanced Indonesia holiday from UAE: nine days across the cool highlands of Puncak, five nights in Bali, and a night in the capital Jakarta — all connected by domestic flights and a private car with an Arabic-speaking driver throughout.

The trip opens in Puncak, a green mountain retreat south of Jakarta, with two nights at the Pesona Alam Resort, a visit to Taman Safari, and the striking Lake of Colors in the tea garden. A domestic flight then takes you to Bali for five nights at the Aston.

Bali balances beaches, culture and adventure: watersports and Pandawa Beach at Tanjung Benoa, river rowing and horseback riding on Saba Beach, the famous Handara Gate in the northern highlands, ATV quad biking, the elephant safari in Ubud, and the POD chocolate factory. The trip closes with a domestic flight back to Jakarta for a night at the Novotel Cikini before your departure.

Daily breakfast, a free SIM card, and all private transfers are included from start to finish.`,
    highlights: [
      'Two nights in the cool highlands of Puncak (Taman Safari + Lake of Colors)',
      'Five nights in Bali at the Aston',
      'The iconic Bali Handara Gate in the northern highlands',
      'Watersports, Turtle Island, Water Blow & Pandawa Beach',
      'River rowing & horseback riding on Saba Beach',
      'ATV quad biking, Ubud elephant safari & POD chocolate factory',
      'Domestic flights included (Jakarta – Bali – Jakarta)',
      'Private car with an Arabic-speaking driver + free SIM card',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Jakarta → Puncak',
        description: 'Arrive at Jakarta (Soekarno-Hatta International Airport). Meet your private Arabic-speaking driver and transfer directly to Puncak in the cool highlands. Check in at Pesona Alam Resort and relax.',
        meals: [],
        accommodation: 'Pesona Alam Resort, Puncak',
      },
      {
        day: 2,
        title: 'Safari Zoo & Lake of Colors',
        description: 'Full day in Puncak: visit Taman Safari, the drive-through safari park, then the tea garden to see the striking Lake of Colors set among the plantations.',
        meals: ['Breakfast'],
        accommodation: 'Pesona Alam Resort, Puncak',
      },
      {
        day: 3,
        title: 'Fly to Bali',
        description: 'Transfer to the airport for your domestic flight to Bali (Ngurah Rai International Airport). Transfer to the Aston and rest, settling in for the Bali leg of your trip.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 4,
        title: 'Watersports & Southern Beaches',
        description: 'A day on the water: the Tanjung Benoa watersports area, a visit to Turtle Island, the dramatic Water Blow at Nusa Dua, and the white sand of Pandawa Beach beneath limestone cliffs.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 5,
        title: 'River Rowing & Saba Beach',
        description: 'Morning river rowing/rafting through Bali\'s green interior, followed by horseback riding along the black sand of Saba Beach — a relaxed, scenic day.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 6,
        title: 'Bali Handara Gate',
        description: 'Head to the northern highlands to see the iconic Bali Handara Gate — the striking candi bentar temple gateway framed by mountains, one of Bali\'s most photographed spots. Cool mountain scenery and lakeside views along the way.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 7,
        title: 'ATV, Ubud Elephant Safari & Chocolate Factory',
        description: 'Adventure and Ubud: ATV quad biking through jungle and rice-field tracks, the elephant safari in Ubud, and a stop at the POD chocolate factory for tastings.',
        meals: ['Breakfast'],
        accommodation: 'Aston, Bali',
      },
      {
        day: 8,
        title: 'Return to Jakarta',
        description: 'Morning at leisure, then transfer to the airport for your domestic flight back to Jakarta. Transfer to the Novotel Cikini in central Jakarta. Evening free to rest or explore.',
        meals: ['Breakfast'],
        accommodation: 'Novotel Cikini, Jakarta',
      },
      {
        day: 9,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and transfer to Jakarta airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Accommodation in 4-star hotels with daily breakfast',
      '2 nights Pesona Alam Resort, Puncak',
      '5 nights Aston, Bali',
      '1 night Novotel Cikini, Jakarta',
      'Domestic flight tickets: Jakarta – Bali – Jakarta',
      'Private car with an Arabic-speaking driver throughout the trip',
      'Sightseeing and tours as per the itinerary',
      'Free SIM card',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Indonesia (available on request)',
      'Lunches and dinners outside the package',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Indonesia visa on arrival (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Pesona Alam Resort', city: 'Puncak', rating: 4 },
      { name: 'Aston', city: 'Bali', rating: 4 },
      { name: 'Novotel Cikini', city: 'Jakarta', rating: 4 },
    ],
    bestTimeToVisit: 'Indonesia is a year-round destination. Bali is driest and sunniest from April to October; Puncak is cool and green all year, with occasional afternoon showers.',
    visaInfo: 'Many nationalities can enter Indonesia via visa on arrival (around USD 35) for up to 30 days. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 8-9hr from Dubai (DXB) to Jakarta (CGK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear for Bali beaches and watersports',
      'A light jacket or layer for cool Puncak and Handara highland evenings',
      'Light, breathable clothing for the tropical lowlands',
      'Reef-safe sunscreen and comfortable walking/adventure shoes',
    ],
    faqs: [
      { q: 'How many nights are in Bali?', a: 'Five nights at the Aston in Bali, plus two nights in Puncak and one night in Jakarta — eight nights in total over nine days.' },
      { q: 'Are international flights included?', a: 'No. The package covers the domestic flights Jakarta–Bali–Jakarta, hotels, transfers, and tours. International flights from the UAE can be arranged separately on request.' },
      { q: 'Is the driver Arabic-speaking?', a: 'Yes. A private car with an Arabic-speaking driver accompanies you throughout the trip in both Puncak/Jakarta and Bali.' },
      { q: 'Do UAE residents need a visa for Indonesia?', a: 'Most nationalities can enter Indonesia on a visa on arrival (around USD 35) for stays up to 30 days. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can adjust activities, upgrade hotels, add a Nusa Penida day trip, or add nights. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- THAILAND (BANGKOK & PATTAYA) ------------------------------------------
  {
    id: 'bangkok-pattaya-holiday-package-from-uae',
    title: 'Bangkok & Pattaya',
    badge: 'Best Value',
    badgeColor: 'teal',
    location: 'Bangkok · Pattaya',
    nights: 5,
    days: 6,
    description: 'Two sides of Thailand — the beaches and parks of Pattaya and the temples and floating markets of Bangkok. 5 nights with tours, breakfast and Arabic-speaking support from AED 2,300.',
    priceFromAED: 2300,
    image: '/images/thailand-bangkok-pattaya.webp',
    imageAlt: 'The Pattaya sign with flamingos beside a lake in a park in Pattaya, Thailand',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Bangkok & Pattaya package (5N/6D). Please share details and current pricing.',
    depositAED: 690,
    heroGallery: [
      {
        url: '/images/thailand-bangkok-pattaya.webp',
        alt: 'The Pattaya sign with flamingos beside a lake in a park in Pattaya, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Grand Palace and Wat Phra Kaew temple complex in Bangkok, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1552550018-5253f8b8c1f5?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Damnoen Saduak floating market with wooden boats of fruit near Bangkok',
      },
      {
        url: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Bangkok city skyline at dusk in Thailand',
      },
    ],
    longDescription: `Thailand in one easy trip from UAE: five nights split between the seaside resort city of Pattaya and the temples, palaces and markets of Bangkok, with all tours, breakfasts, transfers and 24-hour Arabic-speaking support included.

The journey opens in Pattaya with two nights at the A-One New Wing Hotel in a Deluxe Double Room. A full countryside day takes in gardens, a strawberry farm, a cable-car ride, and outlet-mall shopping — a relaxed introduction before the capital.

A road transfer then brings you to Bangkok for three nights at the A-One Bangkok Hotel. Here you explore the historic heart of the city: the Grand Palace, the sacred temples of Wat Phra Kaew and Wat Pho, and a klong (canal) boat tour through old Bangkok. The final full day is spent at the famous Damnoen Saduak floating market, sampling local Thai food from the boats.

Hotel bookings with breakfast, the listed tours, a SIM card with unlimited internet, all government taxes, and 24-hour Arabic customer service are all included.`,
    highlights: [
      'Two nights in Pattaya + three nights in Bangkok (Deluxe Double Rooms)',
      'Pattaya countryside tour: gardens, strawberry farm, cable car & outlet mall',
      'Grand Palace, Wat Phra Kaew and Wat Pho in Bangkok',
      'Klong (canal) boat tour through historic Bangkok',
      'Damnoen Saduak floating market with local cuisine',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival → Pattaya',
        description: 'Airport pickup on arrival in Thailand and private transfer to your hotel in Pattaya. Check in at the A-One New Wing Hotel (Deluxe Double Room) and relax after your flight.',
        meals: [],
        accommodation: 'A-One New Wing Hotel, Pattaya (Deluxe Double Room)',
      },
      {
        day: 2,
        title: 'Pattaya Countryside & Shopping',
        description: 'A full day around Pattaya: landscaped gardens, a strawberry farm, a scenic cable-car ride, and outlet-mall shopping. A relaxed day of nature and photo stops before the capital.',
        meals: ['Breakfast'],
        accommodation: 'A-One New Wing Hotel, Pattaya (Deluxe Double Room)',
      },
      {
        day: 3,
        title: 'Pattaya → Bangkok',
        description: 'After breakfast, private road transfer from Pattaya to Bangkok (approximately 2 hours). Check in at the A-One Bangkok Hotel (Deluxe Double Room). Evening free to explore the surroundings.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel, Bangkok (Deluxe Double Room)',
      },
      {
        day: 4,
        title: 'Historic Bangkok',
        description: 'A historical tour of Bangkok: the magnificent Grand Palace, the sacred Wat Phra Kaew (Temple of the Emerald Buddha) and Wat Pho (Reclining Buddha), followed by a klong (canal) boat tour through the old waterways of the city.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel, Bangkok (Deluxe Double Room)',
      },
      {
        day: 5,
        title: 'Floating Market & Local Cuisine',
        description: 'Visit the famous Damnoen Saduak floating market — wooden boats laden with tropical fruit, food and handicrafts — and sample authentic local Thai cuisine straight from the water.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel, Bangkok (Deluxe Double Room)',
      },
      {
        day: 6,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and private transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast',
      '2 nights A-One New Wing Hotel, Pattaya (Deluxe Double Room)',
      '3 nights A-One Bangkok Hotel, Bangkok (Deluxe Double Room)',
      'Airport pickup and all private transfers (incl. Pattaya ⇄ Bangkok)',
      'Sightseeing and tours as per the itinerary',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Thailand (available on request)',
      'Lunches and dinners outside the package',
      'Optional activities and entrance fees not listed in the itinerary',
      'Thailand visa (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'A-One New Wing Hotel', city: 'Pattaya', rating: 4 },
      { name: 'A-One Bangkok Hotel', city: 'Bangkok', rating: 4 },
    ],
    bestTimeToVisit: 'November to March is the cool, dry season and the most comfortable time to visit Bangkok and Pattaya. The green season (May to October) brings warm afternoon showers and fewer crowds.',
    visaInfo: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short stays. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Bangkok (BKK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Light, breathable clothing for Thailand\'s tropical climate',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Comfortable walking shoes for the city and market tours',
      'Reef-safe sunscreen and a hat',
    ],
    faqs: [
      { q: 'Are international flights included?', a: 'No. The package covers hotels, transfers, tours, and taxes. International flights from the UAE can be arranged separately on request.' },
      { q: 'How do I get between Pattaya and Bangkok?', a: 'A private road transfer between Pattaya and Bangkok (about 2 hours) is included in the package — no domestic flight is needed.' },
      { q: 'Is Arabic-speaking support available?', a: 'Yes. The package includes 24-hour Arabic customer service throughout your trip.' },
      { q: 'Do UAE residents need a visa for Thailand?', a: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short tourist stays. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can add Phuket or Krabi, upgrade hotels, or add nights and activities. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per hotel and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- THAILAND (BANGKOK & PHUKET) -------------------------------------------
  {
    id: 'bangkok-phuket-holiday-package-from-uae',
    title: 'Bangkok & Phuket',
    badge: 'Best Value',
    badgeColor: 'teal',
    location: 'Bangkok · Phuket',
    nights: 7,
    days: 8,
    description: 'Thailand\'s capital and its most famous island — Bangkok\'s temples and Phuket\'s beaches and Phi Phi Islands. 7 nights with domestic flights and Arabic-speaking support from AED 2,900.',
    priceFromAED: 2900,
    image: '/images/thailand-phuket-phi-phi.webp',
    imageAlt: 'A longtail boat in the turquoise waters of Maya Bay, Phi Phi Islands near Phuket, Thailand',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Bangkok & Phuket package (7N/8D). Please share details and current pricing.',
    depositAED: 870,
    heroGallery: [
      {
        url: '/images/thailand-phuket-phi-phi.webp',
        alt: 'A longtail boat in the turquoise waters of Maya Bay, Phi Phi Islands near Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Grand Palace and Wat Phra Kaew temple complex in Bangkok, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1589394760825-3a6a0f2e0f5f?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Patong Beach with turquoise water in Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Bangkok city skyline at dusk in Thailand',
      },
    ],
    longDescription: `The best of Thailand in one trip from UAE: three nights in Bangkok wrapped around four nights on the island of Phuket, connected by domestic flights and backed by 24-hour Arabic-speaking support throughout.

You arrive into Bangkok for a night at the Al Meroz Hotel — a well-known halal-friendly hotel — before a short domestic flight to Phuket the next morning. Four nights at The Marina Phuket Hotel put you close to the island\'s beaches and old town: a day exploring Phuket Town (the Wat Chalong temple, the Big Buddha statue, and the colourful Sino-Portuguese Old Town), a boat trip to the world-famous Phi Phi Islands for swimming and snorkelling, and a day at Patong Beach for watersports.

You then fly back to Bangkok for two nights at the A-One Bangkok Hotel and a full day among the capital\'s historic landmarks — the Grand Palace, Wat Phra Kaew (the Temple of the Emerald Buddha), Wat Pho (the Reclining Buddha), and a boat tour on the Chao Phraya River.

Hotel bookings with breakfast, domestic flights, sightseeing tours, a SIM card with unlimited internet, all government taxes, and 24-hour Arabic customer service are all included.`,
    highlights: [
      'Three nights in Bangkok + four nights on Phuket island',
      'Halal-friendly Al Meroz Hotel on arrival in Bangkok',
      'Phuket Town: Wat Chalong, the Big Buddha & the Old Town',
      'Phi Phi Islands boat trip with swimming and snorkelling',
      'Patong Beach and watersports',
      'Grand Palace, Wat Phra Kaew, Wat Pho & Chao Phraya river boat tour',
      'Domestic flights included (Bangkok – Phuket – Bangkok)',
      'SIM card with unlimited internet + 24-hour Arabic support',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Bangkok',
        description: 'Airport pickup on arrival in Bangkok and private transfer to the Al Meroz Hotel (double/twin room). Rest and settle in after your flight.',
        meals: [],
        accommodation: 'Al Meroz Hotel, Bangkok (Double/Twin Room)',
      },
      {
        day: 2,
        title: 'Bangkok → Phuket',
        description: 'After breakfast, transfer to the airport for your domestic flight to Phuket. On arrival, transfer to The Marina Phuket Hotel (Deluxe Double Room). Afternoon free to relax by the beach.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 3,
        title: 'Phuket Town Tour',
        description: 'A day exploring Phuket: the revered Wat Chalong temple, the 45-metre Big Buddha statue with panoramic island views, and the colourful Sino-Portuguese streets of Phuket Old Town.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 4,
        title: 'Phi Phi Islands Boat Trip',
        description: 'Full-day boat trip to the world-famous Phi Phi Islands — dramatic limestone cliffs, hidden lagoons and clear turquoise water. Time for swimming and snorkelling before returning to Phuket.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 5,
        title: 'Patong Beach & Watersports',
        description: 'A day at Patong Beach, Phuket\'s liveliest stretch of sand, with time for watersports and relaxing by the sea. Evening free to explore the beachfront.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 6,
        title: 'Phuket → Bangkok',
        description: 'After breakfast, transfer to the airport for your domestic flight back to Bangkok. Transfer to the A-One Bangkok Hotel (Deluxe Double Room). Evening free to explore the surroundings.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 7,
        title: 'Historic Bangkok',
        description: 'A tour of Bangkok\'s historic landmarks: the magnificent Grand Palace, the sacred Wat Phra Kaew (Temple of the Emerald Buddha) and Wat Pho (Reclining Buddha), followed by a boat tour on the Chao Phraya River.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 8,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and private transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast',
      '1 night Al Meroz Hotel, Bangkok (Double/Twin Room)',
      '4 nights The Marina Phuket Hotel (Deluxe Double Room)',
      '2 nights A-One Bangkok Hotel (Deluxe Double Room)',
      'Domestic flight tickets: Bangkok – Phuket – Bangkok',
      'Airport pickup and all private transfers',
      'Sightseeing and tours as per the itinerary',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Thailand (available on request)',
      'Lunches and dinners outside the package',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Thailand visa (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Al Meroz Hotel', city: 'Bangkok', rating: 4 },
      { name: 'A-One Bangkok Hotel', city: 'Bangkok', rating: 4 },
      { name: 'The Marina Phuket Hotel', city: 'Phuket', rating: 4 },
    ],
    bestTimeToVisit: 'November to April is the dry season and the best time for Phuket\'s beaches and island trips. Bangkok is warm year-round; the green season (May to October) brings warm afternoon showers.',
    visaInfo: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short stays. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Bangkok (BKK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear and beach gear for Phuket and the Phi Phi Islands',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Light, breathable clothing for Thailand\'s tropical climate',
      'Reef-safe sunscreen, a hat and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Are domestic flights included?', a: 'Yes. The domestic flights Bangkok–Phuket and Phuket–Bangkok are included in the package price. International flights from the UAE can be arranged separately on request.' },
      { q: 'Is the Al Meroz Hotel halal-friendly?', a: 'Yes. The Al Meroz in Bangkok is a well-known halal-friendly hotel, a popular choice for Muslim travellers visiting Thailand.' },
      { q: 'Is Arabic-speaking support available?', a: 'Yes. The package includes 24-hour Arabic customer service throughout your trip.' },
      { q: 'Do UAE residents need a visa for Thailand?', a: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short tourist stays. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can add Krabi or Pattaya, upgrade hotels, or add nights and activities. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- THAILAND (BANGKOK & PHUKET) 9 NIGHTS ----------------------------------
  {
    id: 'bangkok-phuket-9-nights-holiday-package-from-uae',
    title: 'Bangkok & Phuket — 9 Nights',
    badge: 'Best Value',
    badgeColor: 'teal',
    location: 'Bangkok · Phuket',
    nights: 9,
    days: 10,
    description: 'The complete Thailand trip — four nights in Bangkok and five on Phuket, with Phi Phi, James Bond Island and the floating markets. 9 nights, domestic flights and Arabic-speaking support from AED 3,600.',
    priceFromAED: 3600,
    image: '/images/thailand-phi-phi-speedboat.webp',
    imageAlt: 'Speedboat and longtail boats among the limestone cliffs and turquoise water of the Phi Phi Islands near Phuket, Thailand',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Bangkok & Phuket 9 Nights package (9N/10D). Please share details and current pricing.',
    depositAED: 1080,
    heroGallery: [
      {
        url: '/images/thailand-phi-phi-speedboat.webp',
        alt: 'Speedboat and longtail boats among the limestone cliffs and turquoise water of the Phi Phi Islands near Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Grand Palace and Wat Phra Kaew temple complex in Bangkok, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1552550018-5253f8b8c1f5?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Damnoen Saduak floating market with wooden boats of fruit near Bangkok',
      },
      {
        url: 'https://images.unsplash.com/photo-1589394760825-3a6a0f2e0f5f?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Patong Beach with turquoise water in Phuket, Thailand',
      },
    ],
    longDescription: `The complete Thailand holiday from UAE: four nights in Bangkok and five nights on the island of Phuket, connected by domestic flights and backed by 24-hour Arabic-speaking support throughout.

You arrive into Bangkok for a night at the halal-friendly Al Meroz Hotel before a short domestic flight to Phuket. Five nights at The Marina Phuket Hotel give you time to see the island properly: a Phuket Town day (Wat Chalong, the Big Buddha and the Old Town), a boat trip to the famous Phi Phi Islands, a day at Patong Beach for watersports, and a full excursion to James Bond Island and the dramatic sea caves of Phang Nga Bay.

You then fly back to Bangkok for three nights at the A-One Bangkok Hotel: a historical day among the Grand Palace, Wat Phra Kaew and Wat Pho with a klong (canal) boat tour, and a morning at the famous Damnoen Saduak floating market before your departure.

Hotel bookings with breakfast, domestic flights, sightseeing tours, a SIM card with unlimited internet, all government taxes, and 24-hour Arabic customer service are all included.`,
    highlights: [
      'Four nights in Bangkok + five nights on Phuket island',
      'Halal-friendly Al Meroz Hotel on arrival in Bangkok',
      'Phi Phi Islands boat trip with swimming and snorkelling',
      'James Bond Island & the sea caves of Phang Nga Bay',
      'Phuket Town (Wat Chalong, Big Buddha, Old Town) & Patong Beach',
      'Grand Palace, Wat Phra Kaew, Wat Pho & klong boat tour',
      'Damnoen Saduak floating market',
      'Domestic flights included + SIM card + 24-hour Arabic support',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Bangkok',
        description: 'Airport pickup on arrival in Bangkok and private transfer to the Al Meroz Hotel (double/twin room). Rest and settle in after your flight.',
        meals: [],
        accommodation: 'Al Meroz Hotel, Bangkok (Double/Twin Room)',
      },
      {
        day: 2,
        title: 'Bangkok → Phuket',
        description: 'After breakfast, transfer to the airport for your domestic flight to Phuket. Transfer to The Marina Phuket Hotel (Deluxe Double Room). Afternoon free to relax by the beach.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 3,
        title: 'Phuket Town Tour',
        description: 'A day exploring Phuket: the revered Wat Chalong temple, the 45-metre Big Buddha statue with panoramic views, and the colourful Sino-Portuguese streets of Phuket Old Town.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 4,
        title: 'Phi Phi Islands Boat Trip',
        description: 'Full-day boat trip to the world-famous Phi Phi Islands — limestone cliffs, hidden lagoons and clear turquoise water. Time for swimming and snorkelling.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 5,
        title: 'Patong Beach & Watersports',
        description: 'A day at Patong Beach, Phuket\'s liveliest stretch of sand, with time for watersports and relaxing by the sea.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 6,
        title: 'James Bond Island & Phang Nga Bay',
        description: 'A full excursion to James Bond Island and Phang Nga Bay — towering limestone karsts rising from emerald water — with time to explore the sea caves and hidden lagoons by boat.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 7,
        title: 'Phuket → Bangkok',
        description: 'After breakfast, transfer to the airport for your domestic flight back to Bangkok. Transfer to the A-One Bangkok Hotel (Deluxe Double Room). Evening free.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 8,
        title: 'Historic Bangkok',
        description: 'A tour of Bangkok\'s historic landmarks: the Grand Palace, Wat Phra Kaew (Temple of the Emerald Buddha) and Wat Pho (Reclining Buddha), followed by a klong (canal) boat tour.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 9,
        title: 'Floating Market',
        description: 'Visit the famous Damnoen Saduak floating market — wooden boats laden with tropical fruit, food and handicrafts — and sample authentic local Thai cuisine straight from the water.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 10,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and private transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast',
      '1 night Al Meroz Hotel, Bangkok (Double/Twin Room)',
      '5 nights The Marina Phuket Hotel (Deluxe Double Room)',
      '3 nights A-One Bangkok Hotel (Deluxe Double Room)',
      'Domestic flight tickets: Bangkok – Phuket – Bangkok',
      'Airport pickup and all private transfers',
      'Sightseeing and tours as per the itinerary',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Thailand (available on request)',
      'Lunches and dinners outside the package',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Thailand visa (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Al Meroz Hotel', city: 'Bangkok', rating: 4 },
      { name: 'A-One Bangkok Hotel', city: 'Bangkok', rating: 4 },
      { name: 'The Marina Phuket Hotel', city: 'Phuket', rating: 4 },
    ],
    bestTimeToVisit: 'November to April is the dry season and the best time for Phuket\'s beaches and island trips. Bangkok is warm year-round; the green season (May to October) brings warm afternoon showers.',
    visaInfo: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short stays. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Bangkok (BKK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear and beach gear for Phuket, Phi Phi and Phang Nga Bay',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Light, breathable clothing for Thailand\'s tropical climate',
      'Reef-safe sunscreen, a hat and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Are domestic flights included?', a: 'Yes. The domestic flights Bangkok–Phuket and Phuket–Bangkok are included in the package price. International flights from the UAE can be arranged separately on request.' },
      { q: 'What is the difference from the 7-night Bangkok & Phuket trip?', a: 'This 9-night version adds an extra night in both Bangkok and Phuket, plus two more experiences: the James Bond Island / Phang Nga Bay excursion and the Damnoen Saduak floating market.' },
      { q: 'Is the Al Meroz Hotel halal-friendly?', a: 'Yes. The Al Meroz in Bangkok is a well-known halal-friendly hotel, a popular choice for Muslim travellers visiting Thailand.' },
      { q: 'Do UAE residents need a visa for Thailand?', a: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short tourist stays. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can add Krabi or Pattaya, upgrade hotels, or add nights and activities. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- THAILAND (BANGKOK & PHUKET) 11 NIGHTS ---------------------------------
  {
    id: 'bangkok-phuket-11-nights-holiday-package-from-uae',
    title: 'Bangkok & Phuket — 11 Nights',
    badge: 'Grand Tour',
    badgeColor: 'gold',
    location: 'Bangkok · Phuket',
    nights: 11,
    days: 12,
    description: 'The grand Thailand tour — six nights on Phuket and five in Bangkok, with Phi Phi, James Bond Island, a jungle safari, floating markets and Bangkok shopping. 11 nights from AED 3,950.',
    priceFromAED: 3950,
    image: '/images/thailand-phuket-beach.webp',
    imageAlt: 'Travellers on a rocky beach with clear turquoise water in Phuket, Thailand',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Bangkok & Phuket 11 Nights package (11N/12D). Please share details and current pricing.',
    depositAED: 1185,
    heroGallery: [
      {
        url: '/images/thailand-phuket-beach.webp',
        alt: 'Travellers on a rocky beach with clear turquoise water in Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Grand Palace and Wat Phra Kaew temple complex in Bangkok, Thailand',
      },
      {
        url: '/images/thailand-phuket-phi-phi.webp',
        alt: 'Maya Bay and the Phi Phi Islands with turquoise water near Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1552550018-5253f8b8c1f5?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Damnoen Saduak floating market with wooden boats of fruit near Bangkok',
      },
      {
        url: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Bangkok city skyline at dusk in Thailand',
      },
    ],
    longDescription: `The grand Thailand tour from UAE: eleven nights split between the island of Phuket and the capital Bangkok, connected by domestic flights and backed by 24-hour Arabic-speaking support throughout — our most complete Thailand itinerary.

After a night at the halal-friendly Al Meroz Hotel in Bangkok, a domestic flight takes you to Phuket for six nights at The Marina Phuket Hotel. There is time for everything the island is famous for: Phuket Town (Wat Chalong, the Big Buddha and the Old Town), the Phi Phi Islands, Patong Beach and watersports, a James Bond Island and Phang Nga Bay sea-cave excursion, and a jungle safari with an elephant ride and the Ton Sai Falls.

You then fly back to Bangkok for four nights at the A-One Bangkok Hotel: the historic Grand Palace, Wat Phra Kaew and Wat Pho; the Damnoen Saduak floating market; and a full Bangkok shopping day across Chatuchak Weekend Market, Siam Paragon mall and the Pratunam district.

Hotel bookings with breakfast, domestic flights, sightseeing tours, a SIM card with unlimited internet, all government taxes, and 24-hour Arabic customer service are all included.`,
    highlights: [
      'Six nights on Phuket + five nights in Bangkok',
      'Phi Phi Islands & James Bond Island / Phang Nga Bay',
      'Jungle safari with elephant ride & Ton Sai Falls',
      'Phuket Town, Big Buddha, Wat Chalong & Patong Beach',
      'Grand Palace, Wat Phra Kaew & Wat Pho',
      'Damnoen Saduak floating market',
      'Bangkok shopping: Chatuchak Market, Siam Paragon & Pratunam',
      'Domestic flights + SIM card + 24-hour Arabic support',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Bangkok',
        description: 'Airport pickup on arrival in Bangkok and private transfer to the Al Meroz Hotel (Superior Double/Twin Room). Rest and settle in after your flight.',
        meals: [],
        accommodation: 'Al Meroz Hotel, Bangkok (Superior Double/Twin Room)',
      },
      {
        day: 2,
        title: 'Bangkok → Phuket',
        description: 'After breakfast, transfer to the airport for your domestic flight to Phuket. Transfer to The Marina Phuket Hotel (Deluxe Double Room). Afternoon free to relax by the beach.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 3,
        title: 'Phuket Town Tour',
        description: 'Explore Phuket: the Wat Chalong temple, the 45-metre Big Buddha statue with panoramic views, and the Sino-Portuguese streets of Phuket Old Town.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 4,
        title: 'Phi Phi Islands Boat Trip',
        description: 'Full-day boat trip to the Phi Phi Islands — limestone cliffs, hidden lagoons and clear turquoise water — with time for swimming and snorkelling.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 5,
        title: 'Patong Beach & Watersports',
        description: 'A day at Patong Beach with watersports and time by the sea, followed by dinner out in the evening.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 6,
        title: 'James Bond Island & Phang Nga Bay',
        description: 'A full excursion to James Bond Island and Phang Nga Bay — towering limestone karsts rising from emerald water — with time to explore the sea caves and hidden lagoons by boat.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 7,
        title: 'Jungle Safari & Ton Sai Falls',
        description: 'A day in Phuket\'s green interior: a jungle safari with an elephant ride and a visit to the Ton Sai Falls in Khao Phra Thaeo national park.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 8,
        title: 'Phuket → Bangkok',
        description: 'After breakfast, transfer to the airport for your domestic flight back to Bangkok. Transfer to the A-One Bangkok Hotel (Deluxe Double Room). Evening free.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 9,
        title: 'Historic Bangkok',
        description: 'A tour of Bangkok\'s historic landmarks: the Grand Palace, Wat Phra Kaew (Temple of the Emerald Buddha) and Wat Pho (Reclining Buddha).',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 10,
        title: 'Floating Market',
        description: 'Visit the famous Damnoen Saduak floating market — wooden boats laden with tropical fruit, food and handicrafts — and sample authentic local Thai cuisine from the water.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 11,
        title: 'Bangkok Shopping Day',
        description: 'A full Bangkok shopping day: the vast Chatuchak Weekend Market, the upscale Siam Paragon mall, and the bargain fashion stalls of the Pratunam district.',
        meals: ['Breakfast'],
        accommodation: 'A-One Bangkok Hotel (Deluxe Double Room)',
      },
      {
        day: 12,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and private transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast',
      '1 night Al Meroz Hotel, Bangkok (Superior Double/Twin Room)',
      '6 nights The Marina Phuket Hotel (Deluxe Double Room)',
      '4 nights A-One Bangkok Hotel (Deluxe Double Room)',
      'Domestic flight tickets: Bangkok – Phuket – Bangkok',
      'Airport pickup and all private transfers',
      'Sightseeing and tours as per the itinerary',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Thailand (available on request)',
      'Lunches and dinners outside the package (except the Day 5 dinner)',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Thailand visa (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Al Meroz Hotel', city: 'Bangkok', rating: 4 },
      { name: 'A-One Bangkok Hotel', city: 'Bangkok', rating: 4 },
      { name: 'The Marina Phuket Hotel', city: 'Phuket', rating: 4 },
    ],
    bestTimeToVisit: 'November to April is the dry season and the best time for Phuket\'s beaches and island trips. Bangkok is warm year-round; the green season (May to October) brings warm afternoon showers.',
    visaInfo: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short stays. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Bangkok (BKK), direct or via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 16 travelers; larger groups on request',
    whatToBring: [
      'Swimwear and beach gear for Phuket, Phi Phi and Phang Nga Bay',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Light, breathable clothing for Thailand\'s tropical climate',
      'Reef-safe sunscreen, a hat and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Are domestic flights included?', a: 'Yes. The domestic flights Bangkok–Phuket and Phuket–Bangkok are included in the package price. International flights from the UAE can be arranged separately on request.' },
      { q: 'How is this different from the shorter Bangkok & Phuket trips?', a: 'This 11-night grand tour adds a jungle safari with an elephant ride and Ton Sai Falls in Phuket, and a full Bangkok shopping day (Chatuchak, Siam Paragon and Pratunam) on top of the 9-night itinerary.' },
      { q: 'Is the Al Meroz Hotel halal-friendly?', a: 'Yes. The Al Meroz in Bangkok is a well-known halal-friendly hotel, a popular choice for Muslim travellers visiting Thailand.' },
      { q: 'Do UAE residents need a visa for Thailand?', a: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short tourist stays. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can the itinerary be customized?', a: 'Yes. You can add Krabi or Pattaya, upgrade hotels, or adjust nights and activities. Contact us on WhatsApp and we will tailor the package to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per airline, hotel, and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- THAILAND (PHUKET ONLY) 7 NIGHTS ---------------------------------------
  {
    id: 'phuket-holiday-package-from-uae',
    title: 'Phuket Island Escape',
    badge: 'Beach Escape',
    badgeColor: 'teal',
    location: 'Phuket',
    nights: 7,
    days: 8,
    description: 'Seven nights across three Phuket hotels — a plunge-pool suite, the beaches, Phi Phi and James Bond Island. Breakfast, tours and Arabic-speaking support from AED 3,100.',
    priceFromAED: 3100,
    image: '/images/thailand-phuket-cove.webp',
    imageAlt: 'A secluded golden sand cove with turquoise water and green hills on Phuket island, Thailand',
    categories: ['family', 'beach', 'honeymoon'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Phuket Island Escape (7N/8D). Please share details and current pricing.',
    depositAED: 930,
    heroGallery: [
      {
        url: '/images/thailand-phuket-cove.webp',
        alt: 'A secluded golden sand cove with turquoise water and green hills on Phuket island, Thailand',
      },
      {
        url: '/images/thailand-phuket-phi-phi.webp',
        alt: 'Maya Bay and the Phi Phi Islands with turquoise water near Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1589394760825-3a6a0f2e0f5f?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Patong Beach with turquoise water in Phuket, Thailand',
      },
      {
        url: '/images/thailand-phi-phi-speedboat.webp',
        alt: 'Speedboat among the limestone cliffs of Phang Nga Bay near Phuket, Thailand',
      },
    ],
    longDescription: `A dedicated Phuket beach holiday from UAE: seven nights on Thailand\'s most famous island, moving across three well-chosen hotels so you experience different sides of Phuket — all with breakfast, tours, and 24-hour Arabic-speaking support.

You begin with two nights at the Indochine Resort and Villas in a Sky Suite with your own plunge pool, and a day exploring Phuket\'s landmarks: the Wat Chalong temple, the Big Buddha, and the Sino-Portuguese streets of Phuket Old Town. You then move to The Marina Phuket Hotel for three nights, with a full-day boat trip to the Phi Phi Islands (swimming, snorkelling and lunch) and a day at Patong Beach for watersports.

For the final two nights you stay at the Marina Gallery Resort on Kalim Bay, near Kamala, with an excursion to James Bond Island and the dramatic sea caves of Phang Nga Bay.

Hotel bookings with breakfast, sightseeing tours, a SIM card with unlimited internet, all government taxes, and 24-hour Arabic customer service are all included.`,
    highlights: [
      'Three Phuket hotels, including a Sky Suite with a private plunge pool',
      'Phuket landmarks: Wat Chalong, the Big Buddha & the Old Town',
      'Phi Phi Islands boat trip with swimming, snorkelling & lunch',
      'Patong Beach and watersports',
      'James Bond Island & the sea caves of Phang Nga Bay',
      'Kalim / Kamala Bay beachfront on the final nights',
      'SIM card with unlimited internet + 24-hour Arabic support',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Phuket',
        description: 'Airport pickup on arrival in Phuket and private transfer to the Indochine Resort and Villas. Check in to your Sky Suite with a private plunge pool and relax.',
        meals: [],
        accommodation: 'Indochine Resort and Villas, Phuket (Sky Suite with Plunge Pool)',
      },
      {
        day: 2,
        title: 'Phuket Landmarks Tour',
        description: 'A day exploring Phuket\'s main sights: the revered Wat Chalong temple, the 45-metre Big Buddha statue with panoramic island views, and the colourful streets of Phuket Old Town.',
        meals: ['Breakfast'],
        accommodation: 'Indochine Resort and Villas, Phuket (Sky Suite with Plunge Pool)',
      },
      {
        day: 3,
        title: 'Move to The Marina Phuket',
        description: 'After breakfast, transfer to The Marina Phuket Hotel (Deluxe Double Room). Afternoon free to enjoy the beach and hotel.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 4,
        title: 'Phi Phi Islands Boat Trip',
        description: 'Full-day boat trip to the Phi Phi Islands — limestone cliffs, hidden lagoons and clear turquoise water — with swimming, snorkelling and lunch included.',
        meals: ['Breakfast', 'Lunch'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 5,
        title: 'Patong Beach & Watersports',
        description: 'A day at Patong Beach, Phuket\'s liveliest stretch of sand, with time for watersports and relaxing by the sea.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel (Deluxe Double Room)',
      },
      {
        day: 6,
        title: 'Move to Marina Gallery, Kalim Bay',
        description: 'After breakfast, transfer to the Marina Gallery Resort on Kalim Bay near Kamala (Deluxe Room). Afternoon free to enjoy the quieter beachfront.',
        meals: ['Breakfast'],
        accommodation: 'Marina Gallery Resort, Kalim Bay (Deluxe Room)',
      },
      {
        day: 7,
        title: 'James Bond Island & Phang Nga Bay',
        description: 'A full excursion to James Bond Island and Phang Nga Bay — towering limestone karsts rising from emerald water — with time to explore the sea caves and hidden lagoons by boat.',
        meals: ['Breakfast'],
        accommodation: 'Marina Gallery Resort, Kalim Bay (Deluxe Room)',
      },
      {
        day: 8,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and private transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast',
      '2 nights Indochine Resort and Villas (Sky Suite with Plunge Pool)',
      '3 nights The Marina Phuket Hotel (Deluxe Double Room)',
      '2 nights Marina Gallery Resort, Kalim Bay (Deluxe Room)',
      'Airport pickup and all private transfers',
      'Sightseeing and tours as per the itinerary (incl. Phi Phi lunch)',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Phuket (available on request)',
      'Lunches and dinners outside the package (except the Day 4 Phi Phi lunch)',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Thailand visa (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Indochine Resort and Villas', city: 'Phuket', rating: 5 },
      { name: 'The Marina Phuket Hotel', city: 'Phuket', rating: 4 },
      { name: 'Marina Gallery Resort, Kalim Bay', city: 'Phuket', rating: 4 },
    ],
    bestTimeToVisit: 'November to April is the dry season and the best time for Phuket\'s beaches and island trips. The green season (May to October) is quieter with warm afternoon showers.',
    visaInfo: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short stays. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Phuket (HKT), usually via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 12 travelers; larger groups on request',
    whatToBring: [
      'Swimwear and beach gear for Phuket, Phi Phi and Phang Nga Bay',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Light, breathable clothing for Thailand\'s tropical climate',
      'Reef-safe sunscreen, a hat and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Why does the trip use three different hotels?', a: 'Each hotel gives you a different side of Phuket — a plunge-pool suite to start, a central beach hotel for the island trips, and a quieter Kalim/Kamala bay resort to finish. All transfers between them are included.' },
      { q: 'Is lunch included on the Phi Phi trip?', a: 'Yes. The full-day Phi Phi Islands boat trip on Day 4 includes lunch, along with swimming and snorkelling.' },
      { q: 'Is Arabic-speaking support available?', a: 'Yes. The package includes 24-hour Arabic customer service throughout your trip.' },
      { q: 'Do UAE residents need a visa for Thailand?', a: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short tourist stays. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can I add Bangkok to this trip?', a: 'Yes. We also offer combined Bangkok & Phuket packages, or we can add Bangkok nights to this itinerary. Contact us on WhatsApp and we will tailor it to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per hotel and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- THAILAND (PHUKET ONLY) 5 NIGHTS ---------------------------------------
  {
    id: 'phuket-5-nights-holiday-package-from-uae',
    title: 'Phuket Getaway — 5 Nights',
    badge: 'Beach Escape',
    badgeColor: 'teal',
    location: 'Phuket',
    nights: 5,
    days: 6,
    description: 'A short Phuket beach break — the island landmarks, a Phi Phi Islands boat trip, and Patong Beach. 5 nights with breakfast, tours and Arabic-speaking support from AED 2,150.',
    priceFromAED: 2150,
    image: '/images/thailand-phuket-longtails.webp',
    imageAlt: 'Traditional longtail boats moored in the clear turquoise sea off Phuket, Thailand',
    categories: ['family', 'beach', 'short'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Phuket Getaway 5 Nights package (5N/6D). Please share details and current pricing.',
    depositAED: 645,
    heroGallery: [
      {
        url: '/images/thailand-phuket-longtails.webp',
        alt: 'Traditional longtail boats moored in the clear turquoise sea off Phuket, Thailand',
      },
      {
        url: '/images/thailand-phuket-phi-phi.webp',
        alt: 'Maya Bay and the Phi Phi Islands with turquoise water near Phuket, Thailand',
      },
      {
        url: 'https://images.unsplash.com/photo-1589394760825-3a6a0f2e0f5f?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Patong Beach with turquoise water in Phuket, Thailand',
      },
      {
        url: '/images/thailand-phuket-cove.webp',
        alt: 'A secluded golden sand cove with turquoise water on Phuket island, Thailand',
      },
    ],
    longDescription: `A relaxed short break on Thailand\'s most famous island: five nights in Phuket across two well-placed hotels, with the island\'s highlights, a Phi Phi Islands boat trip and Patong Beach — all with breakfast, tours and 24-hour Arabic-speaking support.

You start with two nights at the Marina Gallery Resort and a day exploring Phuket\'s landmarks — the Wat Chalong temple, the Big Buddha, and the Sino-Portuguese streets of Phuket Old Town. You then move to The Marina Phuket Hotel for three nights, with a full-day boat trip to the Phi Phi Islands (swimming, snorkelling and lunch) and a day at Patong Beach for watersports, finishing with dinner on the beach.

Hotel bookings with breakfast, sightseeing tours, a SIM card with unlimited internet, all government taxes, and 24-hour Arabic customer service are all included.`,
    highlights: [
      'Five nights in Phuket across two beach hotels',
      'Phuket landmarks: Wat Chalong, the Big Buddha & the Old Town',
      'Phi Phi Islands boat trip with swimming, snorkelling & lunch',
      'Patong Beach with watersports and a beach dinner',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival in Phuket',
        description: 'Airport pickup on arrival in Phuket and private transfer to the Marina Gallery Resort. Check in and relax after your flight.',
        meals: [],
        accommodation: 'Marina Gallery Resort, Phuket',
      },
      {
        day: 2,
        title: 'Phuket Landmarks Tour',
        description: 'A day exploring Phuket: the revered Wat Chalong temple, the 45-metre Big Buddha statue with panoramic island views, and the colourful streets of Phuket Old Town.',
        meals: ['Breakfast'],
        accommodation: 'Marina Gallery Resort, Phuket',
      },
      {
        day: 3,
        title: 'Move to The Marina Phuket',
        description: 'After breakfast, transfer to The Marina Phuket Hotel. Afternoon free to enjoy the beach and hotel facilities.',
        meals: ['Breakfast'],
        accommodation: 'The Marina Phuket Hotel',
      },
      {
        day: 4,
        title: 'Phi Phi Islands Boat Trip',
        description: 'Full-day boat trip to the Phi Phi Islands — limestone cliffs, hidden lagoons and clear turquoise water — with swimming, snorkelling and lunch included.',
        meals: ['Breakfast', 'Lunch'],
        accommodation: 'The Marina Phuket Hotel',
      },
      {
        day: 5,
        title: 'Patong Beach & Watersports',
        description: 'A day at Patong Beach, Phuket\'s liveliest stretch of sand, with time for watersports and relaxing by the sea, finishing with dinner on the beach.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'The Marina Phuket Hotel',
      },
      {
        day: 6,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and private transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Hotel bookings with daily breakfast',
      '2 nights Marina Gallery Resort, Phuket',
      '3 nights The Marina Phuket Hotel',
      'Airport pickup and all private transfers',
      'Sightseeing and tours as per the itinerary (incl. Phi Phi lunch & beach dinner)',
      'SIM card with unlimited internet',
      '24-hour Arabic-speaking customer service',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Phuket (available on request)',
      'Lunches and dinners outside the package (except the Day 4 lunch and Day 5 dinner)',
      'Watersports and optional activity fees not listed in the itinerary',
      'Entrance fees to attractions not mentioned in the itinerary',
      'Thailand visa (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Marina Gallery Resort', city: 'Phuket', rating: 4 },
      { name: 'The Marina Phuket Hotel', city: 'Phuket', rating: 4 },
    ],
    bestTimeToVisit: 'November to April is the dry season and the best time for Phuket\'s beaches and island trips. The green season (May to October) is quieter with warm afternoon showers.',
    visaInfo: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short stays. We confirm your specific entry requirements before travel.',
    flightDuration: 'approx 6-7hr from Dubai (DXB) to Phuket (HKT), usually via one stop',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 12 travelers; larger groups on request',
    whatToBring: [
      'Swimwear and beach gear for Phuket and the Phi Phi Islands',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Light, breathable clothing for Thailand\'s tropical climate',
      'Reef-safe sunscreen, a hat and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Are meals included beyond breakfast?', a: 'Daily breakfast is included, plus lunch on the Phi Phi Islands boat trip (Day 4) and dinner on Patong Beach (Day 5). Other lunches and dinners are at your own cost.' },
      { q: 'Why does the trip use two hotels?', a: 'The two hotels give you a change of scene within Phuket; all transfers between them are included. You can also request a single-hotel stay if you prefer.' },
      { q: 'Is Arabic-speaking support available?', a: 'Yes. The package includes 24-hour Arabic customer service throughout your trip.' },
      { q: 'Do UAE residents need a visa for Thailand?', a: 'Many nationalities can enter Thailand visa-free or via visa on arrival for short tourist stays. We confirm the exact requirement for your passport before you travel.' },
      { q: 'Can I extend the trip or add Bangkok?', a: 'Yes. We offer longer Phuket stays and combined Bangkok & Phuket packages. Contact us on WhatsApp and we will tailor it to your dates and budget.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per hotel and supplier policies. Non-refundable services cannot be refunded once confirmed.',
  },

  // -- SRI LANKA 11 NIGHTS ---------------------------------------------------
  {
    id: 'sri-lanka-11-nights-holiday-package-from-uae',
    title: 'Sri Lanka Grand Tour — 11 Nights',
    badge: 'Grand Tour',
    badgeColor: 'gold',
    location: 'Sri Lanka',
    nights: 11,
    days: 12,
    description: 'The full island loop — Sigiriya, Kandy, Nuwara Eliya tea country, Bentota beach and Colombo. 11 nights half-board with a private Arabic-speaking driver from AED 3,650.',
    priceFromAED: 3650,
    image: '/images/sri-lanka-nine-arch-bridge.webp',
    imageAlt: 'The blue Sri Lankan train crossing the Nine Arch Bridge through green tea-country hills near Ella, Sri Lanka',
    categories: ['family', 'beach'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Sri Lanka Grand Tour (11N/12D). Please share details and current pricing.',
    depositAED: 1095,
    heroGallery: [
      {
        url: '/images/sri-lanka-nine-arch-bridge.webp',
        alt: 'The blue Sri Lankan train crossing the Nine Arch Bridge through green tea-country hills near Ella, Sri Lanka',
      },
      {
        url: 'https://images.unsplash.com/photo-1546975490-e8b92a360b24?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Green tea plantations in the hill country near Nuwara Eliya, Sri Lanka',
      },
      {
        url: 'https://images.unsplash.com/photo-1580889240912-c39ce7b0f66a?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Sigiriya Lion Rock fortress rising above the jungle in Sri Lanka',
      },
    ],
    longDescription: `A complete Sri Lanka island tour from UAE: eleven nights looping through the cultural triangle, the central hill country, the southwest beaches and the capital, with a private car and an Arabic-speaking driver throughout, and half-board (breakfast and dinner) at every hotel.

You begin in Sigiriya in the cultural heartland (two nights), then move up to Kandy — the hill capital and home of the Temple of the Sacred Tooth Relic (two nights). From there you climb into the cool tea country of Nuwara Eliya (two nights) before descending to the golden southwest coast at Bentota for three nights of beach time. The tour finishes with two nights in Colombo, the lively seaside capital.

Your driver takes you between regions and to the sights along the way, and all government taxes are included. This is our Silver package — a well-balanced 4-star itinerary covering the best of the island at a relaxed pace.

Note: this offer is valid for travel between 1 May and 31 October 2026. During the Buddhist festival period (17–28 August) an additional government tax of USD 50 per room per night applies. Hotel check-in is from 2:00 PM and check-out is by 12:00 PM; the listed hotels may be substituted with similar 4-star properties subject to availability.`,
    highlights: [
      'Sigiriya cultural triangle (2 nights)',
      'Kandy — the hill capital & Temple of the Sacred Tooth (2 nights)',
      'Nuwara Eliya tea country (2 nights)',
      'Bentota golden beaches (3 nights)',
      'Colombo, the seaside capital (2 nights)',
      'Half-board: breakfast and dinner at every hotel',
      'Private car with an Arabic-speaking driver throughout',
      'SIM card, airport transfers and all taxes included',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Arrival → Sigiriya',
        description: 'Airport pickup on arrival in Sri Lanka and transfer with your private Arabic-speaking driver to Sigiriya in the cultural heartland. Check in at Hotel Sigiriya and relax.',
        meals: ['Dinner'],
        accommodation: 'Hotel Sigiriya (Half Board)',
      },
      {
        day: 2,
        title: 'Sigiriya & the Cultural Triangle',
        description: 'A day among Sri Lanka\'s ancient wonders — the Sigiriya Lion Rock fortress and the surrounding cultural triangle, with sightseeing arranged by your driver-guide.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Hotel Sigiriya (Half Board)',
      },
      {
        day: 3,
        title: 'Sigiriya → Kandy',
        description: 'Drive to Kandy, the hill capital, stopping at sights along the way. Check in at Mount Blue Kandy. Evening at leisure near the lake.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Mount Blue Kandy (Half Board)',
      },
      {
        day: 4,
        title: 'Kandy',
        description: 'Explore Kandy — the Temple of the Sacred Tooth Relic, the lake, and the city\'s gardens and viewpoints.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Mount Blue Kandy (Half Board)',
      },
      {
        day: 5,
        title: 'Kandy → Nuwara Eliya',
        description: 'Drive up into the cool central highlands to Nuwara Eliya, through terraced tea plantations. Check in at Heaven Seven Nuwara Eliya.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Heaven Seven Nuwara Eliya (Half Board)',
      },
      {
        day: 6,
        title: 'Nuwara Eliya Tea Country',
        description: 'A day in the tea country — a tea plantation and factory visit, cool mountain scenery, and the colonial charm of "Little England".',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Heaven Seven Nuwara Eliya (Half Board)',
      },
      {
        day: 7,
        title: 'Nuwara Eliya → Bentota',
        description: 'Descend from the hills to the southwest coast at Bentota. Check in at Earl\'s Reef Beruwala and unwind on the beach.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Earl\'s Reef Beruwala, Bentota (Half Board)',
      },
      {
        day: 8,
        title: 'Bentota Beach',
        description: 'A relaxed beach day at Bentota, with optional watersports and a river safari on the Bentota lagoon (on request).',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Earl\'s Reef Beruwala, Bentota (Half Board)',
      },
      {
        day: 9,
        title: 'Bentota Beach',
        description: 'A second full day to enjoy the golden southwest coast — swimming, relaxing, or an optional trip to nearby Galle Fort.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'Earl\'s Reef Beruwala, Bentota (Half Board)',
      },
      {
        day: 10,
        title: 'Bentota → Colombo',
        description: 'Drive up the coast to Colombo, the capital. Check in at The Ocean Colombo. Evening free along the seafront.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'The Ocean Colombo (Half Board)',
      },
      {
        day: 11,
        title: 'Colombo City Tour',
        description: 'A city tour of Colombo — the Galle Face seafront, colonial and religious landmarks, markets and shopping.',
        meals: ['Breakfast', 'Dinner'],
        accommodation: 'The Ocean Colombo (Half Board)',
      },
      {
        day: 12,
        title: 'Departure',
        description: 'Breakfast at the hotel. Check out and transfer to the airport for your return flight to the UAE. Our team confirms ground transport arrangements on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Half-board accommodation (breakfast and dinner) at all hotels',
      '2 nights Hotel Sigiriya',
      '2 nights Mount Blue Kandy',
      '2 nights Heaven Seven Nuwara Eliya',
      '3 nights Earl\'s Reef Beruwala, Bentota',
      '2 nights The Ocean Colombo',
      'Private car with an Arabic-speaking driver throughout',
      'Airport pickup and drop-off',
      'Sightseeing at the tourist sites in the itinerary',
      'SIM card with internet',
      'All government taxes',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'International flights from UAE to Sri Lanka (available on request)',
      'Lunches (breakfast and dinner are included)',
      'Buddhist festival government tax of USD 50 per room per night (17–28 August 2026 only)',
      'Optional activities and entrance fees not listed in the itinerary',
      'Sri Lanka visa / ETA (if applicable to your nationality)',
      'Travel insurance and personal expenses',
    ],
    hotelExamples: [
      { name: 'Hotel Sigiriya', city: 'Sigiriya', rating: 4 },
      { name: 'Mount Blue Kandy', city: 'Kandy', rating: 4 },
      { name: 'Heaven Seven Nuwara Eliya', city: 'Nuwara Eliya', rating: 4 },
      { name: 'Earl\'s Reef Beruwala', city: 'Bentota', rating: 4 },
      { name: 'The Ocean Colombo', city: 'Colombo', rating: 4 },
    ],
    bestTimeToVisit: 'This offer runs 1 May to 31 October 2026 — the best season for the southwest coast (Bentota) and the cultural triangle. The hill country is cool and green year-round.',
    visaInfo: 'Most nationalities need a Sri Lanka ETA (electronic travel authorisation) before arrival. We advise you on the current requirement and process before travel.',
    flightDuration: 'approx 4-5hr from Dubai (DXB) to Colombo (CMB), direct',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: '2 to 14 travelers; larger groups on request',
    whatToBring: [
      'Light, breathable clothing for the coast and lowlands',
      'A warm layer for the cool hill country (Nuwara Eliya)',
      'Modest dress for temple visits (shoulders and knees covered)',
      'Swimwear for Bentota, sunscreen, and comfortable walking shoes',
    ],
    faqs: [
      { q: 'Are meals included?', a: 'Yes. The package is half-board — breakfast and dinner are included at every hotel. Lunches are at your own cost.' },
      { q: 'Is there an Arabic-speaking driver?', a: 'Yes. A private car with an Arabic-speaking driver accompanies you throughout the whole island tour.' },
      { q: 'What is the Buddhist festival tax?', a: 'For travel during 17–28 August 2026 only, the Sri Lankan government applies an additional tax of USD 50 per room per night. It does not apply to other dates.' },
      { q: 'Do UAE residents need a visa for Sri Lanka?', a: 'Most travellers need a Sri Lanka ETA (electronic travel authorisation) obtained before arrival. We guide you through the current requirement and process before you travel.' },
      { q: 'Can the itinerary or hotels be changed?', a: 'Yes. Hotels may be substituted with similar 4-star properties subject to availability, and we can adjust the itinerary, add nights, or upgrade hotels on request.' },
    ],
    cancellationPolicy: '30% deposit required to confirm booking. Full payment due 15 days before departure. Cancellation charges apply per hotel and supplier policies. Non-refundable services cannot be refunded once confirmed. Offer valid for travel 1 May – 31 October 2026.',
  },

  // -- UMRAH -----------------------------------------------------------------
  {
    id: 'umrah',
    title: 'Umrah Travel Package',
    badge: 'Spiritual Journey',
    badgeColor: 'teal',
    location: 'Makkah & Madinah, Saudi Arabia',
    nights: 5,
    days: 6,
    description: 'A complete Umrah experience - hotel near Haram, return air tickets, transport, and 24/7 group support from UAE.',
    priceFromAED: 1999,
    image: 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?auto=format&fit=crop&w=800&q=80&fm=webp',
    imageAlt: 'The Holy Kaaba surrounded by pilgrims during Umrah',
    categories: ['spiritual'],
    waText: 'Hello Big Bird Travel, I want to inquire about the Umrah Travel Package (5N/6D). Please share details and current pricing.',
    depositAED: 600,
    heroGallery: [
      {
        url: 'https://images.unsplash.com/photo-1565019011521-b0575cbb57c8?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: "Prophet's Mosque (Masjid al-Nabawi) in Madinah at dusk",
      },
      {
        url: 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Aerial view of the Grand Mosque and the Kaaba in Makkah',
      },
      {
        url: 'https://images.unsplash.com/photo-1586724237569-f3d021dd4c37?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Pilgrims circumambulating the Kaaba during Tawaf',
      },
      {
        url: 'https://images.unsplash.com/photo-1565019001729-2b40d79fa898?auto=format&fit=crop&w=1200&q=80&fm=webp',
        alt: 'Clock Tower and Grand Mosque in Makkah at night',
      },
    ],
    longDescription: `Umrah is one of the most spiritually significant journeys a Muslim can undertake - and for UAE residents, the proximity to the Holy Cities of Makkah and Madinah makes it accessible year-round. This five-night package provides everything you need: return air tickets from the UAE, hotel accommodation close to Masjid Al-Haram, intercity transport between Madinah and Makkah, and the guidance of an experienced Umrah-familiar team throughout.

The itinerary follows the traditional sequence: arriving in Madinah first for Ziyarah of the Prophet's Mosque and the historic sites of the city, then travelling to Makkah for the Umrah rituals - Ihram, Tawaf, and Sa'i. Our local ground team in both cities ensures you are met at the airport, transferred safely, and have a point of contact at all times.

Hotels are selected for proximity to the Haram: within a 300-700 metre walk in Makkah, within walking distance of the Prophet's Mosque in Madinah. We also provide a pre-departure briefing on Umrah rituals, ihram requirements, and what to bring - particularly important for first-time performers. This package is available year-round with additional Ramadan-specific arrangements on request.`,
    highlights: [
      'Hotel within 500m of Masjid Al-Haram in Makkah',
      'Hotel within walking distance of the Prophet\'s Mosque in Madinah',
      'Return economy air tickets from Dubai or Abu Dhabi',
      'Private intercity transport: airport <-> Madinah <-> Makkah',
      'Pre-departure Umrah ritual briefing by our team',
      'Ziyarah of Madinah historic sites included',
      '24/7 WhatsApp support throughout the journey',
    ],
    itinerary: [
      {
        day: 1,
        title: 'Depart UAE - Arrive Madinah',
        description: 'Depart from Dubai (DXB) or Abu Dhabi (AUH). Arrive at Madinah (Prince Mohammad Bin Abdulaziz Airport). Immigration and customs. Private transfer to hotel near the Prophet\'s Mosque. Check in, rest, and offer the first prayers at Masjid al-Nabawi.',
        meals: [],
        accommodation: '3-Star Hotel, Madinah (near Prophet\'s Mosque)',
      },
      {
        day: 2,
        title: 'Madinah - Ziyarah',
        description: 'Full day in Madinah. Morning prayers at Masjid al-Nabawi followed by Ziyarah of the Prophet\'s (PBUH) tomb. Guided Ziyarah tour to the historic sites: Quba Mosque (first mosque in Islam), Masjid al-Qiblatayn, Uhud Mountain and the graves of the martyrs of Uhud, and Masjid al-Juma. Evening free for personal ibadah.',
        meals: ['Breakfast'],
        accommodation: '3-Star Hotel, Madinah (near Prophet\'s Mosque)',
      },
      {
        day: 3,
        title: 'Madinah -> Makkah',
        description: 'Morning prayers and farewell time at the Prophet\'s Mosque. Mid-morning: private transport to Makkah (approximately 4-5 hours via the Haramain Express or private vehicle). Enter Ihram at the Miqat. Arrive Makkah. Hotel check-in. Evening: first Tawaf and Sa\'i at your own pace after rest.',
        meals: ['Breakfast'],
        accommodation: '3-Star Hotel, Makkah (within 500m of Masjid Al-Haram)',
      },
      {
        day: 4,
        title: 'Umrah Rituals',
        description: 'Complete the Umrah: Tawaf al-Umrah (7 circuits of the Kaaba), then Sa\'i (7 laps between Safa and Marwa), then Halq or Taqsir to exit Ihram. Afternoon and evening free for additional Tawaf, Quran, and personal prayer. Our local guide is available to accompany you if requested.',
        meals: ['Breakfast'],
        accommodation: '3-Star Hotel, Makkah (within 500m of Masjid Al-Haram)',
      },
      {
        day: 5,
        title: 'Free Day in Makkah',
        description: 'Full day for additional ibadah, Nafl Tawaf, and visits to Jabal al-Nour (Cave of Hira) and Jabal Thawr for those who wish. Makkah Museum and the Zamzam Well area. Evening farewell prayers at Masjid Al-Haram.',
        meals: ['Breakfast'],
        accommodation: '3-Star Hotel, Makkah (within 500m of Masjid Al-Haram)',
      },
      {
        day: 6,
        title: 'Departure from Jeddah',
        description: 'Check-out from Makkah hotel. Private transfer to King Abdulaziz International Airport, Jeddah (JED). Return flight to UAE. Our team confirms ground transport on the UAE side.',
        meals: ['Breakfast'],
      },
    ],
    inclusions: [
      'Return economy air tickets (UAE <-> Madinah or Jeddah)',
      '2 nights in Madinah - 3-star hotel near Prophet\'s Mosque',
      '3 nights in Makkah - 3-star hotel within 500m of Masjid Al-Haram',
      'Daily breakfast',
      'Private airport transfers in both Madinah and Jeddah',
      'Private intercity transport Madinah <-> Makkah',
      'Guided Madinah Ziyarah tour (Day 2)',
      'Pre-departure Umrah briefing by our team',
      '24/7 WhatsApp support from Big Bird Travel',
    ],
    exclusions: [
      'Lunches and dinners (halal options widely available near both mosques)',
      'Saudi Arabia visa (Umrah visa - we assist with the application process)',
      'Ihram clothing (available for purchase before departure or in Makkah)',
      'Optional Jabal al-Nour and Jabal Thawr guided tour',
      'Travel insurance',
    ],
    hotelExamples: [
      { name: 'Movenpick Hotel & Residences Hajar Tower', city: 'Makkah', rating: 5 },
      { name: 'Al Safwah Royale Orchid Hotel', city: 'Makkah', rating: 4 },
      { name: 'Anwar Al Madinah Movenpick Hotel', city: 'Madinah', rating: 5 },
    ],
    bestTimeToVisit: 'Umrah can be performed year-round. Avoid peak Hajj season (Dhul Hijja). Ramadan is spiritually rewarding but very crowded; book 3+ months in advance.',
    visaInfo: 'An Umrah visa is required for Saudi Arabia. We assist with the Umrah visa application through the Nusuk platform. Most UAE residents receive the visa within 24-72 hours.',
    flightDuration: 'approx 2hr from Dubai (DXB) to Madinah (MED) or Jeddah (JED)',
    departureCities: ['Dubai (DXB)', 'Abu Dhabi (AUH)', 'Sharjah (SHJ)'],
    groupSize: 'Individual travelers to groups of 50+',
    whatToBring: [
      'Ihram garments (two unstitched white cloths for men; modest modest clothing for women)',
      'Unscented soap, shampoo, and toiletries (scented products not permitted during Ihram)',
      'Comfortable, non-slip sandals for Tawaf and Sa\'i',
      'Small Quran and supplication book',
      'Saudi Riyal cash for meals and personal expenses',
    ],
    faqs: [
      { q: 'Do you help with the Umrah visa?', a: 'Yes. We assist with the Umrah visa application through the Nusuk platform. Most UAE residents receive approval within 24-72 hours. We walk you through the process step by step.' },
      { q: 'How close are the hotels to the Haram?', a: 'Our standard packages include hotels within a 300-700 metre walking distance of Masjid Al-Haram in Makkah and within 10 minutes\' walk of Masjid al-Nabawi in Madinah. Closer options are available as upgrades.' },
      { q: 'Can women travel without a mahram?', a: 'Saudi Arabia now permits women over 45 to perform Umrah in an organized group without a mahram. Women under 45 must travel with a mahram. We can advise on the current regulations applicable to your situation.' },
      { q: 'Is this package available during Ramadan?', a: 'Yes, Ramadan packages are available. Ramadan is the most spiritually rewarding time for Umrah but also the most crowded. Prices increase significantly. Book at least 3 months in advance.' },
      { q: 'What is the Ihram requirement?', a: 'Men must wear two unstitched white sheets; women should be modestly dressed (face veil and gloves are not required for Ihram). We provide a detailed pre-departure briefing covering all Ihram rules and the Umrah rituals.' },
    ],
    cancellationPolicy: 'Umrah packages: 50% refund for cancellations more than 30 days before departure. No refund within 30 days. Visa application fees are non-refundable once submitted.',
  },
];

// -- PACKAGES for listing page (derived from PACKAGE_DETAILS, 3 highlights each) -

export const PACKAGES: Package[] = PACKAGE_DETAILS.map(
  ({
    heroGallery: _hg, longDescription: _ld, itinerary: _it,
    inclusions: _inc, exclusions: _ex, hotelExamples: _he,
    bestTimeToVisit: _btv, visaInfo: _vi, flightDuration: _fd,
    departureCities: _dc, groupSize: _gs, whatToBring: _wtb,
    faqs: _fq, cancellationPolicy: _cp, depositAED: _dep,
    highlights, ...base
  }) => ({
    ...base,
    highlights: highlights.slice(0, 3),
  }),
);

// -- Destination band ----------------------------------------------------------

export const DESTINATIONS: Destination[] = [
  {
    name: 'Georgia',
    tagline: 'Mountains & Old Town',
    image: 'https://images.unsplash.com/photo-1565008447742-97f6f38c985c?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Snow-capped Caucasus mountains in Georgia',
    href: '/destinations/georgia',
    waText: q('Hello Big Bird Travel, I want to plan a trip to Georgia from UAE. Please share package options.'),
  },
  {
    name: 'Azerbaijan',
    tagline: 'Baku & Caspian Coast',
    image: 'https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Baku Flame Towers illuminated at night above the old city',
    href: '/tour-packages',
    waText: q('Hello Big Bird Travel, I want to plan a trip to Azerbaijan from UAE. Please share package options.'),
  },
  {
    name: 'Turkey',
    tagline: 'Bazaars & Balloons',
    image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Cappadocia hot air balloons over fairy chimneys',
    href: '/destinations/turkey',
    waText: q('Hello Big Bird Travel, I want to plan a trip to Turkey from UAE. Please share package options.'),
  },
  {
    name: 'Maldives',
    tagline: 'Overwater Paradise',
    image: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Aerial view of Maldives atoll with overwater villas',
    href: '/destinations/maldives',
    waText: q('Hello Big Bird Travel, I want to plan a trip to the Maldives from UAE. Please share package options.'),
  },
  {
    name: 'Bali',
    tagline: 'Temples & Rice Terraces',
    image: 'https://images.unsplash.com/photo-1604999333679-b86d54738315?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Tanah Lot temple on a rock in Bali at sunset',
    href: '/destinations/bali',
    waText: q('Hello Big Bird Travel, I want to plan a trip to Bali from UAE. Please share package options.'),
  },
  {
    name: 'Thailand',
    tagline: 'Beaches & Street Food',
    image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Phi Phi Islands turquoise water in Thailand',
    href: '/tour-packages',
    waText: q('Hello Big Bird Travel, I want to plan a trip to Thailand from UAE. Please share package options.'),
  },
  {
    name: 'Malaysia',
    tagline: 'Culture & Cuisine',
    image: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Petronas Twin Towers at night in Kuala Lumpur',
    href: '/tour-packages/malaysia-holiday-packages-from-uae',
    waText: q('Hello Big Bird Travel, I want to plan a trip to Malaysia from UAE. Please share package options.'),
  },
  {
    name: 'Saudi Arabia',
    tagline: 'Spiritual & Heritage',
    image: 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?auto=format&fit=crop&w=1200&q=80&fm=webp',
    imageAlt: 'Aerial view of the Grand Mosque and the Kaaba in Makkah',
    href: '/umrah',
    waText: q('Hello Big Bird Travel, I want to plan an Umrah or Saudi Arabia trip from UAE. Please share package options.'),
  },
];

// -- Price table ---------------------------------------------------------------

export const PRICE_TABLE: PriceRow[] = [
  { destination: 'Georgia',                        duration: '5 Nights / 6 Days', priceAED: 1399, type: 'Family, Short Break' },
  { destination: 'Azerbaijan',                     duration: '4 Nights / 5 Days', priceAED: 1599, type: 'Short Break, Cultural', popular: true },
  { destination: 'Turkey - Istanbul',              duration: '5 Nights / 6 Days', priceAED: 2299, type: 'Family, Couples' },
  { destination: 'Turkey - Istanbul & Cappadocia', duration: '8 Nights / 9 Days', priceAED: 3199, type: 'Couples, Honeymoon' },
  { destination: 'Thailand - Bangkok',             duration: '5 Nights / 6 Days', priceAED: 1899, type: 'Family, Budget' },
  { destination: 'Malaysia - Kuala Lumpur',        duration: '5 Nights / 6 Days', priceAED: 1799, type: 'Family, Cultural' },
  { destination: 'Thailand - Bangkok & Pattaya',   duration: '5 Nights / 6 Days', priceAED: 2300, type: 'Family, Beach' },
  { destination: 'Thailand - Bangkok & Phuket',    duration: '7 Nights / 8 Days', priceAED: 2900, type: 'Family, Beach' },
  { destination: 'Thailand - Bangkok & Phuket',    duration: '9 Nights / 10 Days', priceAED: 3600, type: 'Family, Beach' },
  { destination: 'Thailand - Bangkok & Phuket',    duration: '11 Nights / 12 Days', priceAED: 3950, type: 'Family, Beach' },
  { destination: 'Thailand - Phuket Island',       duration: '5 Nights / 6 Days', priceAED: 2150, type: 'Beach, Short' },
  { destination: 'Thailand - Phuket Island',       duration: '7 Nights / 8 Days', priceAED: 3100, type: 'Beach, Honeymoon' },
  { destination: 'Malaysia - Selangor & Kuala Lumpur', duration: '5 Nights / 6 Days', priceAED: 2200, type: 'Family, Cultural' },
  { destination: 'Malaysia - Selangor, Langkawi & KL', duration: '7 Nights / 8 Days', priceAED: 3100, type: 'Family, Beach', popular: true },
  { destination: 'Malaysia - Selangor, Langkawi & KL', duration: '9 Nights / 10 Days', priceAED: 3600, type: 'Family, Beach' },
  { destination: 'Indonesia - Jakarta, Puncak & Bali', duration: '7 Nights / 8 Days', priceAED: 2750, type: 'Family, Beach', popular: true },
  { destination: 'Indonesia - Jakarta, Puncak & Bali', duration: '8 Nights / 9 Days', priceAED: 3200, type: 'Family, Beach' },
  { destination: 'Indonesia - Jakarta, Puncak & Bali', duration: '9 Nights / 10 Days', priceAED: 3360, type: 'Family, Beach' },
  { destination: 'Bali - Standard',                duration: '5 Nights / 6 Days', priceAED: 2499, type: 'Family, Couples' },
  { destination: 'Bali - Premium',                 duration: '7 Nights / 8 Days', priceAED: 3799, type: 'Honeymoon, Luxury', popular: true },
  { destination: 'Maldives - Budget Resort',       duration: '4 Nights / 5 Days', priceAED: 2999, type: 'Couples, Beach' },
  { destination: 'Maldives - Premium Villa',       duration: '5 Nights / 6 Days', priceAED: 4499, type: 'Honeymoon, Luxury', popular: true },
  { destination: 'Sri Lanka - Grand Tour',         duration: '11 Nights / 12 Days', priceAED: 3650, type: 'Family, Beach' },
  { destination: 'Umrah Package',                  duration: '5 Nights / 6 Days', priceAED: 1999, type: 'Spiritual, Family' },
];

// -- Testimonials --------------------------------------------------------------

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Fatima Al-Rashid',
    origin: 'Dubai, UAE',
    text: 'Our Maldives honeymoon was absolutely perfect. Big Bird Travel handled everything from the overwater villa to the seaplane transfers. Zero stress, maximum romance.',
    rating: 5,
    packageName: 'Maldives Honeymoon Escape',
  },
  {
    name: 'Mohammed Khalil',
    origin: 'Abu Dhabi, UAE',
    text: 'Took my whole family to Georgia - 2 adults, 3 kids. The team customized the itinerary to suit everyone. The Kazbegi mountain drive was breathtaking. Booking again this year.',
    rating: 5,
    packageName: 'Georgia Mountain & City',
  },
  {
    name: 'Priya Sundaram',
    origin: 'Sharjah, UAE',
    text: "The Turkey trip exceeded all expectations. Istanbul, Cappadocia, the balloon ride - unforgettable. Big Bird's WhatsApp support was incredibly responsive throughout.",
    rating: 5,
    packageName: 'Turkey Cultural Journey',
  },
];

// -- Listing-page FAQs ---------------------------------------------------------

export const FAQS = [
  { q: 'Do you offer holiday packages from UAE?', a: 'Yes, Big Bird Travel & Tourism UAE helps customers plan holiday packages from the UAE, including flights, hotels, tours, transfers, visa assistance, and custom itinerary support depending on the destination.' },
  { q: 'Can I get a customized holiday package?', a: 'Yes, packages can be customized based on your destination, travel dates, number of travelers, budget, hotel category, and preferred activities. We do not use fixed templates.' },
  { q: 'Do package prices include flights and hotels?', a: 'Package inclusions depend on the selected offer. Some packages include flights, hotels, transfers, tours, or visa assistance. Our team clearly explains what is included before you confirm any booking.' },
  { q: 'Which destinations are popular from UAE?', a: 'Popular destinations from the UAE include Georgia, Azerbaijan, Turkey, Maldives, Bali, Thailand, Malaysia, Saudi Arabia, and UAE tour experiences such as Desert Safari and Dubai City Tour.' },
  { q: 'Can you help with visa assistance?', a: 'Yes, visa assistance is available for many destinations. Visa approval is subject to the decision of the relevant immigration authority or embassy. We guide you through the process and document requirements.' },
  { q: 'How can I get a package quote?', a: 'Contact Big Bird Travel & Tourism UAE on WhatsApp with your destination, travel dates, number of travelers, and budget. Our team will respond with available package options, pricing, and inclusions.' },
];
